import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { toast } from "sonner";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import { formatDate } from "@/lib/dates";
import {
  ReceiptText, FileText, Download, Plus, Loader2, Trash2, RefreshCcw, BedDouble, Mail, CheckCircle2,
  Edit3, Ban, RotateCcw, Eye,
  ChevronDown, ChevronRight, Folder, Calculator, MessageCircle, Search, X, Globe, BookOpen, User, Star, Bell,
} from "lucide-react";

// === Helpers ===
const fmtLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayISO = () => fmtLocal(new Date());
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return fmtLocal(d); };

// ============================================================
// SECTION 3: Quote Extras CRUD (gestione voci preset preventivo + import Guide)
// ============================================================
const QUOTE_EXTRA_DEFAULT = { name: "", qty_mode: "flat", unit_price: 0, sort_order: 0, active: true };
const GUIDE_BASE_URL = "https://booking-guide-plus.preview.emergentagent.com";

const QuoteExtrasSection = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(QUOTE_EXTRA_DEFAULT);
  const [importing, setImporting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // id voce in attesa di conferma cancellazione
  const editorRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/quote-extras");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      if (editingId) {
        await api.put(`/admin/quote-extras/${editingId}`, draft);
        toast.success("Aggiornato");
      } else {
        await api.post("/admin/quote-extras", draft);
        toast.success("Voce creata");
      }
      setEditingId(null);
      setDraft(QUOTE_EXTRA_DEFAULT);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setDraft({
      name: it.name || "",
      qty_mode: it.qty_mode || "flat",
      unit_price: Number(it.unit_price || 0),
      sort_order: Number(it.sort_order || 0),
      active: it.active !== false,
    });
    // Scroll all'editor + focus sul campo nome così l'host vede subito che la modifica è iniziata.
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const nameInput = editorRef.current.querySelector('[data-testid="qe-name"]');
        if (nameInput) nameInput.focus();
      }
    }, 50);
  };

  const remove = async (id) => {
    // Niente `window.confirm`: viene bloccato silenziosamente dentro iframe
    // sandboxed (es. preview Emergent senza `allow-modals`) ritornando undefined,
    // facendo abortire la DELETE. Uso una conferma inline a 2 click.
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    try {
      await api.delete(`/admin/quote-extras/${id}`);
      toast.success("Eliminata");
      setConfirmDeleteId(null);
      load();
    } catch { toast.error("Errore"); }
  };

  // Cleanup: chiama l'endpoint backend `/admin/quote-extras/dedupe` che
  // elimina i duplicati per nome (case-insensitive) in modo atomico. Funziona
  // anche se il frontend non riesce a leggere la lista correttamente.
  const dedupeAll = async () => {
    try {
      const r = await api.post("/admin/quote-extras/dedupe");
      const n = r.data?.deleted || 0;
      if (n === 0) toast.info("Nessun duplicato trovato");
      else toast.success(`${n} duplicati eliminati`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore pulizia duplicati"));
    }
  };

  // Import dalla Guide App esterna (booking-guide-plus...). Carica la lista
  // dei servizi pubblici esposti dall'app guida e permette di selezionarli
  // per importarli come quote_extras. Mappatura: nome → name, prezzo → unit_price,
  // qty_mode "flat" di default (l'host può modificarlo dopo).
  const [guideServices, setGuideServices] = useState(null);
  const [guidePicked, setGuidePicked] = useState({});
  const fetchGuide = async () => {
    setImporting(true);
    try {
      // Tentativi multipli sugli endpoint pubblici plausibili.
      let data = null; let lastErr = null;
      for (const path of ["/api/services", "/api/public/services", "/api/services/public"]) {
        try {
          const r = await fetch(`${GUIDE_BASE_URL}${path}`, { mode: "cors" });
          if (r.ok) { data = await r.json(); break; }
          lastErr = `${r.status} ${path}`;
        } catch (e) { lastErr = e.message; }
      }
      if (!data) {
        toast.error(`App Guida non raggiungibile (${lastErr || "no endpoint"}). Inserisci manualmente.`);
        setGuideServices([]);
        return;
      }
      const raw = Array.isArray(data) ? data : (data.services || data.items || []);
      // Normalizza + filtra + dedupe per nome (case-insensitive).
      // Il campo `price` dell'App Guida può essere stringa numerica ("15.50"),
      // numero (15.5), o testo libero ("Su preventivo", "Costo prodotto", "").
      // Lo parsifico in modo permissivo: estraggo il primo numero trovato,
      // altrimenti marco la voce come "su richiesta" (prezzo 0 + flag).
      const parsePrice = (rawPrice) => {
        if (rawPrice === null || rawPrice === undefined) return { value: 0, onRequest: true };
        if (typeof rawPrice === "number" && !Number.isNaN(rawPrice)) return { value: rawPrice, onRequest: false };
        const s = String(rawPrice).trim();
        if (!s) return { value: 0, onRequest: true };
        // Estrae il primo numero (es. "15,50 €" → 15.50, "€ 25" → 25)
        const m = s.replace(",", ".").match(/-?\d+(?:\.\d+)?/);
        if (!m) return { value: 0, onRequest: true }; // "Su preventivo", "Costo prodotto", ...
        const n = parseFloat(m[0]);
        return Number.isNaN(n) ? { value: 0, onRequest: true } : { value: n, onRequest: false };
      };
      const seen = new Set();
      const list = [];
      for (const s of raw) {
        const name = (s.name || s.title || s.label || "").trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const priceRaw = s.price ?? s.unit_price ?? s.amount ?? s.cost ?? s.prezzo;
        const { value, onRequest } = parsePrice(priceRaw);
        list.push({ ...s, _normName: name, _price: value, _onRequest: onRequest });
      }
      if (raw.length && !list.length) console.warn("[QuoteExtras] Guide services found but none had a name. First item:", raw[0]);
      setGuideServices(list);
      const picked = {}; list.forEach((s) => { picked[s._normName.toLowerCase()] = true; });
      setGuidePicked(picked);
    } finally { setImporting(false); }
  };
  const doImport = async () => {
    if (!guideServices || importing) return;
    setImporting(true);
    const toImport = guideServices.filter((s) => guidePicked[s._normName.toLowerCase()]);
    let ok = 0; let updated = 0;
    for (const s of toImport) {
      try {
        const r = await api.post("/admin/quote-extras", {
          name: s._normName,
          unit_price: Number(s._price) || 0,
          qty_mode: "flat",
          sort_order: items.length + ok + 1,
          active: true,
        });
        if (r.data?.updated) updated += 1;
        ok += 1;
      } catch (e) {
        console.warn("Import voce extra fallito:", s._normName, e?.response?.data || e?.message);
      }
    }
    const newOnes = ok - updated;
    if (ok === 0) {
      toast.error("Nessun servizio importato. Controlla la console per i dettagli.");
    } else {
      toast.success(`Import OK — ${newOnes} nuovi, ${updated} aggiornati (totale ${ok}/${toImport.length})`);
    }
    setGuideServices(null); setGuidePicked({});
    setImporting(false);
    load();
  };

  return (
    <div className="bg-white rounded-lg border border-[#E8E2D9] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAF7F2] transition"
        data-testid="qe-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#AC5D45]" />
          <span className="font-display text-base text-[#1E3A5F]">Servizi extra preventivo</span>
          <span className="text-[10px] text-[#635F59] font-normal">({items.length} configurati)</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="p-4 space-y-3 border-t border-[#E8E2D9]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-sm text-[#635F59] flex-1 min-w-[200px]">
              Configura i servizi extra che appariranno nel dropdown "<b>+ Servizio extra</b>" dei Preventivi PDF.
              Puoi anche importare in massa quelli configurati nell'App Guida.
            </p>
            {items.length > 0 && (
              <Button onClick={dedupeAll} size="sm" variant="outline" className="text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="qe-dedupe">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Pulisci duplicati
              </Button>
            )}
          </div>

          {/* Editor: nuovo / modifica voce */}
          <div
            ref={editorRef}
            className={`bg-[#FAF7F2] border rounded p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end transition-all ${editingId ? "border-[#AC5D45] ring-2 ring-[#AC5D45]/30 shadow-md" : "border-[#E8E2D9]"}`}
            data-testid="qe-editor"
          >
            {editingId && (
              <div className="sm:col-span-12 -mb-1 flex items-center gap-2 text-[11px] font-semibold text-[#AC5D45]">
                <Edit3 className="w-3.5 h-3.5" />
                Stai modificando una voce esistente. Clicca "Aggiorna" per salvare o ✕ per annullare.
              </div>
            )}
            <div className="sm:col-span-4">
              <Label className="text-[10px] text-[#635F59]">Nome servizio</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Es. Trasferimento porto" data-testid="qe-name" />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[10px] text-[#635F59]">Calcolo quantità</Label>
              <select
                value={draft.qty_mode}
                onChange={(e) => setDraft({ ...draft, qty_mode: e.target.value })}
                className="h-9 w-full text-xs rounded border border-[#E8E2D9] bg-white px-2"
                data-testid="qe-qty-mode"
              >
                <option value="flat">Quantità fissa (1)</option>
                <option value="per_night">Per ogni notte</option>
                <option value="per_guest">Per ogni ospite</option>
                <option value="per_guest_per_night">Per ospite × notte (es. tassa di soggiorno)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] text-[#635F59]">Prezzo unitario (€)</Label>
              <Input type="number" step="0.01" min="0" value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: parseFloat(e.target.value) || 0 })} data-testid="qe-price" />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-[10px] text-[#635F59]">Ord.</Label>
              <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })} />
            </div>
            <div className="sm:col-span-2 flex items-center gap-1.5">
              <Button onClick={save} size="sm" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white flex-1" data-testid="qe-save">
                {editingId ? "Aggiorna" : "Aggiungi"}
              </Button>
              {editingId && (
                <Button onClick={() => { setEditingId(null); setDraft(QUOTE_EXTRA_DEFAULT); }} size="sm" variant="outline">✕</Button>
              )}
            </div>
          </div>

          {/* Import dalla Guide App */}
          <div className="bg-[#FFF4D6] border border-[#E3A857] rounded p-3" data-testid="qe-import">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs font-semibold text-[#7A5410] flex items-center gap-1.5">
                <RefreshCcw className="w-3.5 h-3.5" /> Importa servizi dall'App Guida
              </Label>
              <Button onClick={fetchGuide} disabled={importing} size="sm" variant="outline" className="text-[11px]" data-testid="qe-fetch-guide">
                {importing ? "Carico..." : "Carica lista"}
              </Button>
            </div>
            {guideServices !== null && (
              <div className="mt-2">
                {guideServices.length === 0 ? (
                  <p className="text-[11px] text-[#7A5410]">Nessun servizio trovato nell'App Guida.</p>
                ) : (
                  <>
                    <div className="max-h-48 overflow-auto space-y-1 mb-2">
                      {guideServices.map((s, idx) => {
                        const k = s._normName.toLowerCase();
                        return (
                          <label key={k || idx} className="flex items-center gap-2 text-xs bg-white px-2 py-1 rounded border border-[#E3A857]/50">
                            <input type="checkbox" checked={!!guidePicked[k]} onChange={() => setGuidePicked({ ...guidePicked, [k]: !guidePicked[k] })} />
                            <span className="flex-1 truncate">{s._normName}</span>
                            {s._onRequest ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[#FFE5D9] text-[#7A5410] rounded">su richiesta</span>
                            ) : (
                              <span className="text-[#7A5410] font-semibold">€ {Number(s._price || 0).toFixed(2)}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#7A5410] mb-2 italic">
                      Le voci marcate "su richiesta" verranno importate con prezzo €0,00 — modificalo manualmente dopo l'import.
                    </p>
                    <Button onClick={doImport} disabled={importing} size="sm" className="bg-[#7A5410] hover:bg-[#5A3D0C] text-white disabled:opacity-50">
                      {importing ? "Importo..." : "Importa selezionati"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Lista voci esistenti */}
          {loading ? (
            <p className="text-xs text-[#635F59]">Carico...</p>
          ) : (
            <div className="space-y-1">
              {/* Banner duplicati: visibile quando rileva voci con stesso nome */}
              {(() => {
                const counts = {};
                items.forEach((it) => {
                  const k = (it.name || "").trim().toLowerCase();
                  if (k) counts[k] = (counts[k] || 0) + 1;
                });
                const dupCount = Object.values(counts).filter((n) => n > 1).reduce((a, n) => a + (n - 1), 0);
                if (dupCount === 0) return null;
                return (
                  <div className="bg-rose-50 border border-rose-300 rounded p-2.5 flex items-center justify-between gap-2 mb-2" data-testid="qe-dup-banner">
                    <span className="text-xs text-rose-800">
                      ⚠ Rilevati <b>{dupCount}</b> duplicati nella lista (voci con lo stesso nome).
                    </span>
                    <Button onClick={dedupeAll} size="sm" className="bg-rose-600 hover:bg-rose-700 text-white text-[11px] h-7" data-testid="qe-dedupe-banner">
                      <Trash2 className="w-3 h-3 mr-1" /> Pulisci {dupCount} duplicati
                    </Button>
                  </div>
                );
              })()}
              {items.map((it) => (
                <div key={it.id} className={`grid grid-cols-12 gap-2 items-center border rounded px-2 py-1.5 text-xs transition-colors ${editingId === it.id ? "bg-[#FFF4D6] border-[#AC5D45]" : "bg-white border-[#E8E2D9]"}`} data-testid={`qe-row-${it.id}`}>
                  <div className="col-span-5 font-semibold text-[#1E3A5F] truncate">
                    {it.name}
                    {!it.active && <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">disattivato</span>}
                  </div>
                  <div className="col-span-3 text-[10px] text-[#635F59]">
                    {({ flat: "Q. fissa", per_night: "× notte", per_guest: "× ospite", per_guest_per_night: "× osp × notte" })[it.qty_mode] || it.qty_mode}
                  </div>
                  <div className="col-span-2 text-right font-semibold text-[#AC5D45]">€ {Number(it.unit_price || 0).toFixed(2)}</div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => startEdit(it)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#FAF7F2]" title="Modifica" data-testid={`qe-edit-${it.id}`}>
                      <Edit3 className="w-3.5 h-3.5 text-[#1E3A5F]" />
                    </button>
                    {confirmDeleteId === it.id ? (
                      <>
                        <button onClick={() => remove(it.id)} className="text-[10px] px-1.5 py-0.5 bg-rose-600 hover:bg-rose-700 text-white rounded font-semibold" title="Confermare eliminazione" data-testid={`qe-del-confirm-${it.id}`}>
                          Conferma
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} className="text-[10px] px-1.5 py-0.5 bg-gray-200 hover:bg-gray-300 rounded" title="Annulla">
                          ✕
                        </button>
                      </>
                    ) : (
                      <button onClick={() => remove(it.id)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50" title="Elimina" data-testid={`qe-del-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-xs text-[#635F59] italic">Nessun servizio configurato.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default function ReceiptsQuotesTab() {
  return (
    <section className="space-y-6" data-testid="receipts-quotes-tab">
      <header className="bg-gradient-to-r from-emerald-700 to-emerald-500 text-white rounded-xl p-5">
        <h3 className="font-display text-2xl mb-1 flex items-center gap-2">
          <ReceiptText className="w-6 h-6" />
          Ricevute & Preventivi
        </h3>
        <p className="text-sm text-white/85 leading-relaxed">
          Genera ricevute non fiscali per gli ospiti e preventivi in PDF da inviare via email/WhatsApp.
          L'archivio salva tutto, così puoi riscaricare in qualsiasi momento.
        </p>
      </header>

      <ReceiptsSection />
      <QuotesSection />
      <QuoteExtrasSection />
    </section>
  );
}

// ============================================================
// Shared PDF preview modal — replaces `window.open(blobUrl)` which
// Chrome blocks via popup blocker / ERR_BLOCKED_BY_CLIENT (adblocker).
// Renders the PDF inline using an <iframe> so the admin never leaves
// the dashboard and pop-up settings don't matter.
// ============================================================
const PdfPreviewDialog = ({ open, onClose, blobUrl, filename }) => {
  // Per il pulsante "Scarica", se l'URL contiene `inline=1` (modalità anteprima)
  // genero la versione "attachment" rimuovendo quel parametro.
  const downloadUrl = blobUrl ? blobUrl.replace(/[?&]inline=1\b/, "") : "";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] h-[88vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-4 py-2 border-b bg-[#FAF7F2] flex-row items-center justify-between space-y-0 gap-2">
          <DialogTitle className="text-sm font-display text-[#1E3A5F] truncate flex-1 min-w-0">
            {filename || "Anteprima PDF"}
          </DialogTitle>
          <div className="flex items-center gap-2 shrink-0">
            {blobUrl && (
              <a
                href={downloadUrl}
                download={filename || "documento.pdf"}
                className="text-xs px-3 py-1.5 rounded bg-[#AC5D45] hover:bg-[#8A4936] text-white inline-flex items-center gap-1"
                data-testid="pdf-preview-download"
              >
                <Download className="w-3.5 h-3.5" /> Scarica
              </a>
            )}
            {/* Big red close button — replaces the tiny default Dialog X that
                was barely visible on mobile (host feedback iter220). */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi anteprima"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1"
              data-testid="pdf-preview-close"
            >
              <X className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-gray-100">
          {blobUrl ? (
            <iframe
              src={blobUrl}
              title={filename || "PDF"}
              className="w-full h-full border-0"
              data-testid="pdf-preview-iframe"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-[#635F59]">Caricamento…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// SECTION 1: Ricevute (archivio unificato sito + guida + manuale)
// ============================================================
const SOURCE_META = {
  website: { label: "Sito", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Globe },
  guide_app: { label: "Guida", color: "bg-violet-100 text-violet-800 border-violet-200", icon: BookOpen },
  manual: { label: "Manuale", color: "bg-amber-100 text-amber-800 border-amber-200", icon: User },
};

const ReceiptsSection = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [kind, setKind] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stayFrom, setStayFrom] = useState("");
  const [stayTo, setStayTo] = useState("");
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename } | null

  // iter173: leggi query param `?manual=1&q=CODE` per aprire il dialog
  // "Nuova ricevuta manuale" con la ricerca pre-riempita. Usato dal
  // pulsante "Crea ricevuta" del BookingRow (day detail del calendario)
  // per evitare che l'host debba cambiare tab e ricercare a mano.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const manual = url.searchParams.get("manual");
      const qParam = url.searchParams.get("q");
      if (manual === "1") {
        if (qParam) setQ(qParam);
        setOpen(true);            // expand the "Ricevute" panel
        setShowManualDialog(true); // open "Nuova ricevuta manuale"
        // Pulisce i query param senza ricaricare la pagina.
        url.searchParams.delete("manual");
        url.searchParams.delete("q");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* noop */ }
  }, []);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (source && source !== "all") params.source = source;
      if (kind && kind !== "all") params.kind = kind;
      if (fromDate) params.date_from = fromDate;
      if (toDate) params.date_to = toDate;
      if (stayFrom) params.stay_from = stayFrom;
      if (stayTo) params.stay_to = stayTo;
      const r = await api.get("/admin/receipts", { params });
      setItems(r.data?.items || []);
    } catch (e) {
      setItems([]);
      toast.error(errMsg(e, "Errore caricamento ricevute"));
    } finally {
      setLoading(false);
    }
  }, [q, source, kind, fromDate, toDate, stayFrom, stayTo]);

  useEffect(() => {
    const t = setTimeout(loadReceipts, 250);
    return () => clearTimeout(t);
  }, [loadReceipts]);

  const downloadPdf = async (rec) => {
    try {
      const r = await api.get(`/admin/receipts/${encodeURIComponent(rec.number)}/pdf`, { responseType: "blob" });
      const safe = (rec.number || "ricevuta").replace(/[^a-zA-Z0-9_-]+/g, "_");
      saveBlob(r.data, `ricevuta_${safe}.pdf`);
    } catch (e) {
      toast.error(errMsg(e, "Errore download"));
    }
  };

  const viewPdf = async (rec) => {
    // Anteprima inline in modale (iframe). Usa l'endpoint backend HTTPS con
    // ?token=<JWT>&inline=1 invece di un blob URL — i blob URL venivano
    // bloccati da adblocker (ERR_BLOCKED_BY_CLIENT) e dalla CSP del browser.
    const t = getToken();
    if (!t) { toast.error("Sessione scaduta. Effettua nuovamente il login."); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/admin/receipts/${encodeURIComponent(rec.number)}/pdf?inline=1&token=${encodeURIComponent(t)}`;
    setPdfPreview({ url, filename: `ricevuta_${rec.number}.pdf` });
  };

  const closePdfPreview = () => {
    setPdfPreview(null);
  };

  const emailReceipt = async (rec) => {
    const cust = rec.customer || {};
    const customerName = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || "il cliente";
    const existing = (cust.email || "").trim();
    // If no email on file, prompt the admin. Otherwise just confirm before sending.
    let recipient = existing;
    if (!existing) {
      recipient = window.prompt(
        `Nessuna email registrata per ${customerName}.\nInserisci l'indirizzo a cui inviare la ricevuta #${rec.number}:`,
        "",
      );
      if (!recipient) return;
    } else {
      if (!window.confirm(`Inviare la ricevuta #${rec.number} a ${existing}?`)) return;
    }
    recipient = (recipient || "").trim();
    if (!recipient.includes("@")) {
      toast.error("Indirizzo email non valido");
      return;
    }
    try {
      const r = await api.post(
        `/admin/receipts/${encodeURIComponent(rec.number)}/email`,
        existing && recipient === existing ? {} : { email: recipient },
      );
      toast.success(`✓ Ricevuta inviata a ${r.data?.sent_to || recipient}`, { duration: 5000 });
      loadReceipts();  // refresh to show "last_emailed_at" if we surface it later
    } catch (e) {
      toast.error(errMsg(e, "Errore invio email"));
    }
  };

  const voidReceipt = async (rec) => {
    const reason = window.prompt(
      `Annullare la ricevuta #${rec.number}?\n\n` +
      `Resterà visibile in archivio MARCATA come ANNULLATA (audit trail conservato).\n` +
      `Inserisci il motivo dell'annullamento (opzionale):`,
      "",
    );
    if (reason === null) return; // user cancelled the prompt
    try {
      await api.post(`/admin/receipts/${encodeURIComponent(rec.number)}/void`, { reason });
      toast.success(`✓ Ricevuta #${rec.number} annullata`);
      loadReceipts();
    } catch (e) {
      toast.error(errMsg(e, "Errore annullamento"));
    }
  };

  const restoreReceipt = async (rec) => {
    if (!window.confirm(`Ripristinare la ricevuta #${rec.number}? Tornerà valida.`)) return;
    try {
      await api.post(`/admin/receipts/${encodeURIComponent(rec.number)}/restore`);
      toast.success(`✓ Ricevuta #${rec.number} ripristinata`);
      loadReceipts();
    } catch (e) {
      toast.error(errMsg(e, "Errore ripristino"));
    }
  };

  const deleteReceipt = async (rec) => {
    if (!window.confirm(
      `Eliminare DEFINITIVAMENTE la ricevuta #${rec.number}?\n\n` +
      `⚠️  Questa azione cancella la ricevuta dall'archivio SENZA lasciare traccia.\n` +
      `Per cancellazioni di routine usa invece "Annulla" (mantiene l'audit).\n\n` +
      `Procedere solo se è un duplicato o un errore di test.`
    )) return;
    try {
      await api.delete(`/admin/receipts/${encodeURIComponent(rec.number)}`);
      toast.success(`Ricevuta #${rec.number} eliminata`);
      loadReceipts();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  const editReceipt = (rec) => {
    // Open the full manual-receipt dialog in edit mode so the admin sees
    // ALL fields at once and can change any of them.
    setEditingReceipt(rec);
  };

  const sendWhatsAppWithPdf = async (rec) => {
    // WhatsApp's `wa.me` URL scheme cannot attach files — only pre-fill text.
    // Workflow: create a public share link to the PDF, then build a message
    // that includes that link, then open the WhatsApp chat for the customer.
    const cust = rec.customer || {};
    if (!cust.phone) {
      toast.error("Numero di telefono mancante sul cliente");
      return;
    }
    const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || "il cliente";
    try {
      // 1. Ensure a public share link exists (idempotent — re-uses an existing token)
      const r = await api.post(`/admin/receipts/${encodeURIComponent(rec.number)}/share-link`);
      const pdfUrl = r.data?.url;
      if (!pdfUrl) throw new Error("Link PDF non disponibile");
      // 2. Build the message with the PDF link appended
      const total = Number(rec.total || 0).toFixed(2);
      const msg = (
        `Buongiorno ${name}, da B&B Emmanuel.\n\n` +
        `In allegato la ricevuta n. ${rec.number} per un importo di € ${total}.\n\n` +
        `📄 Scarica il PDF: ${pdfUrl}\n\n` +
        `Per qualsiasi necessità rispondi pure a questo messaggio. Grazie!`
      );
      // 3. Open WhatsApp with the prefilled message
      const url = buildWhatsAppLink(cust.phone, msg);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up bloccato. Abilitalo per aprire WhatsApp.");
    } catch (e) {
      toast.error(errMsg(e, "Errore preparazione link WhatsApp"));
    }
  };

  const clearFilters = () => {
    setQ(""); setSource("all"); setKind("all"); setFromDate(""); setToDate(""); setStayFrom(""); setStayTo("");
  };

  const hasFilters = q || source !== "all" || kind !== "all" || fromDate || toDate || stayFrom || stayTo;

  return (
    <div className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="receipts-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="receipts-toggle"
      >
        <h4 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
          <ReceiptText className="w-5 h-5 text-emerald-600" /> Ricevute · archivio unificato
          <Badge className="bg-emerald-600 text-white">{items.length}</Badge>
        </h4>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-[#635F59]">
            Archivio centralizzato di tutte le ricevute: generate dal <strong>sito</strong>, sincronizzate dall'
            <strong>App Guida Digitale</strong>, o create <strong>manualmente</strong> da te. Tutte con il logo del B&amp;B.
          </p>

          {/* Filters + manual creation */}
          {/* Tab strip — splits the archive into 4 views. Search/date filters
              above apply across the active view. */}
          <div className="flex flex-wrap gap-1 mb-2 border-b border-[#E8E2D9]" data-testid="rec-tabs">
            {[
              { id: "all", label: "Tutte", icon: ReceiptText, color: "text-[#1E3A5F]" },
              { id: "website", label: "Sito", icon: Globe, color: "text-emerald-700" },
              { id: "guide_app", label: "App Guida", icon: BookOpen, color: "text-[#AC5D45]" },
              { id: "manual", label: "Manuali", icon: User, color: "text-[#1E3A5F]" },
            ].map((t) => {
              const I = t.icon;
              const active = source === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setSource(t.id); if (t.id !== "guide_app") setKind("all"); }}
                  className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                    active
                      ? "border-[#AC5D45] text-[#AC5D45]"
                      : "border-transparent text-[#635F59] hover:text-[#2D2926]"
                  }`}
                  data-testid={`rec-tab-${t.id}`}
                >
                  <I className={`w-4 h-4 ${active ? "text-[#AC5D45]" : t.color}`} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Sub-filter "Tipo" — only visible on the Guide App tab */}
          {source === "guide_app" && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="rec-kind-filter">
              {[
                { id: "all", label: "Tutti i tipi", emoji: "🗂" },
                { id: "tassa_soggiorno", label: "Tassa di soggiorno", emoji: "🏨" },
                { id: "extras", label: "Servizi extra", emoji: "🛎" },
                { id: "misto", label: "Misto", emoji: "🔀" },
              ].map((k) => (
                <button
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    kind === k.id
                      ? "bg-[#AC5D45] text-white border-[#AC5D45]"
                      : "bg-white border-[#E8E2D9] text-[#635F59] hover:border-[#AC5D45]/40"
                  }`}
                  data-testid={`rec-kind-${k.id}`}
                >
                  <span className="mr-1">{k.emoji}</span>
                  {k.label}
                </button>
              ))}
            </div>
          )}

          <div className="bg-[#FAF7F2] border border-[#E8E2D9] rounded-lg p-3 space-y-3">
            {/* Row 1: Text search + source filter + main actions */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Cerca cliente / N° ricevuta / codice prenotazione</Label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9b948a]" />
                  <Input
                    className="pl-7"
                    placeholder="Es. Rossi, mario@…, EMM-2026-0042"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter on the filter input → if no receipts match, open the
                      // "Nuova ricevuta" dialog pre-filled with this search on the
                      // "Archivio prenotazioni" tab. This lets users go from
                      // searching a booking code → creating a receipt from that
                      // booking in one keystroke.
                      if (e.key === "Enter" && q.trim()) {
                        e.preventDefault();
                        setShowManualDialog(true);
                      }
                    }}
                    data-testid="rec-filter-q"
                  />
                </div>
              </div>
              <div className="w-[140px]">
                <Label className="text-xs">Fonte</Label>
                <Select value={source} onValueChange={(v) => { setSource(v); if (v !== "guide_app") setKind("all"); }}>
                  <SelectTrigger data-testid="rec-filter-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="website">Sito</SelectItem>
                    <SelectItem value="guide_app">App Guida</SelectItem>
                    <SelectItem value="manual">Manuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {hasFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="rec-clear-filters">
                  <X className="w-3.5 h-3.5 mr-1" /> Pulisci
                </Button>
              )}
              <Button
                variant="outline"
                className="text-[#1E3A5F] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white ml-auto"
                onClick={async () => {
                  if (!window.confirm("Sincronizzare le ricevute esistenti dall'App Guida Digitale?\n\nUtile per importare ricevute create PRIMA che il webhook fosse attivo.")) return;
                  try {
                    // Timeout esplicito 25s: l'endpoint backend ha un pre-check
                    // 3s + 3 tentativi × 5s = 18s max → 25s lascia margine al
                    // proxy Cloudflare evitando "Network Error" generici.
                    const r = await api.post("/admin/guide/pull-receipts", null, { timeout: 25000 });
                    const d = r.data || {};
                    toast.success(
                      `✓ ${d.imported || 0} nuove · ${d.updated || 0} aggiornate · ${d.skipped || 0} saltate (tot. ${d.guide_total || 0})`,
                      { duration: 6000 },
                    );
                    loadReceipts();
                  } catch (e) {
                    // Distinguo timeout/network da errore server con detail
                    if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
                      toast.error("Timeout: l'App Guida non risponde in tempo. Riprova tra qualche secondo.");
                    } else if (e?.message === "Network Error") {
                      toast.error("Errore di rete. Verifica connessione e che l'App Guida sia online.");
                    } else {
                      toast.error(errMsg(e, "Errore sync ricevute"));
                    }
                  }
                }}
                data-testid="rec-sync-guide"
                title="Importa ricevute esistenti dall'App Guida Digitale"
              >
                <RefreshCcw className="w-4 h-4 mr-1.5" /> Sincronizza dall'App Guida
              </Button>
              <Button
                variant="outline"
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={async () => {
                  const params = { fmt: "csv" };
                  if (q) params.q = q;
                  if (source && source !== "all") params.source = source;
                  if (kind && kind !== "all") params.kind = kind;
                  if (fromDate) params.date_from = fromDate;
                  if (toDate) params.date_to = toDate;
                  if (stayFrom) params.stay_from = stayFrom;
                  if (stayTo) params.stay_to = stayTo;
                  try {
                    const r = await api.get("/admin/receipts/export", { params, responseType: "blob" });
                    const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12);
                    saveBlob(r.data, `ricevute_${ts}.csv`);
                    toast.success("CSV scaricato");
                  } catch (e) {
                    toast.error(errMsg(e, "Errore export CSV"));
                  }
                }}
                data-testid="rec-export-csv"
                title="Esporta CSV (Excel) con i risultati filtrati"
              >
                <Download className="w-4 h-4 mr-1.5" /> CSV
              </Button>
              <Button
                variant="outline"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                onClick={async () => {
                  const params = { fmt: "pdf" };
                  if (q) params.q = q;
                  if (source && source !== "all") params.source = source;
                  if (kind && kind !== "all") params.kind = kind;
                  if (fromDate) params.date_from = fromDate;
                  if (toDate) params.date_to = toDate;
                  if (stayFrom) params.stay_from = stayFrom;
                  if (stayTo) params.stay_to = stayTo;
                  try {
                    const r = await api.get("/admin/receipts/export", { params, responseType: "blob" });
                    const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12);
                    saveBlob(r.data, `ricevute_${ts}.pdf`);
                    toast.success("PDF scaricato");
                  } catch (e) {
                    toast.error(errMsg(e, "Errore export PDF"));
                  }
                }}
                data-testid="rec-export-pdf"
                title="Stampa/Esporta PDF riepilogativo con i risultati filtrati"
              >
                <FileText className="w-4 h-4 mr-1.5" /> PDF
              </Button>
              <Button
                className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white"
                onClick={() => setShowManualDialog(true)}
                data-testid="rec-new-manual"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Nuova ricevuta
              </Button>
            </div>

            {/* Row 2: Date filters (receipt issue date + stay date) */}
            <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-[#E8E2D9]">
              <div className="flex items-end gap-2">
                <ReceiptText className="w-4 h-4 text-emerald-600 mb-2.5" />
                <div className="w-[140px]">
                  <Label className="text-xs">Data ricevuta · da</Label>
                  <DateInputIT value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="rec-filter-from" />
                </div>
                <div className="w-[140px]">
                  <Label className="text-xs">a</Label>
                  <DateInputIT value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="rec-filter-to" />
                </div>
              </div>
              <div className="hidden md:block w-px h-9 bg-[#E8E2D9] mx-2" />
              <div className="flex items-end gap-2">
                <BedDouble className="w-4 h-4 text-[#AC5D45] mb-2.5" />
                <div className="w-[140px]">
                  <Label className="text-xs">Data soggiorno · da</Label>
                  <DateInputIT value={stayFrom} onChange={(e) => setStayFrom(e.target.value)} data-testid="rec-filter-stay-from" />
                </div>
                <div className="w-[140px]">
                  <Label className="text-xs">a</Label>
                  <DateInputIT value={stayTo} onChange={(e) => setStayTo(e.target.value)} data-testid="rec-filter-stay-to" />
                </div>
              </div>
            </div>
          </div>

          {/* List */}
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-[#635F59]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carico…
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-[#9b948a] italic text-sm">
                {hasFilters ? (
                  <>
                    Nessuna ricevuta trovata coi filtri attivi.
                    {q && (
                      <div className="mt-3 not-italic">
                        <button
                          type="button"
                          onClick={() => setShowManualDialog(true)}
                          className="inline-flex items-center gap-1.5 bg-[#AC5D45] hover:bg-[#8B4A37] text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                          data-testid="rec-empty-create-from-q"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Crea ricevuta da &quot;{q}&quot;
                        </button>
                        <div className="text-[10px] text-[#9b948a] mt-1.5">
                          Cerca questa prenotazione nell&apos;archivio e compila il form automaticamente.
                        </div>
                      </div>
                    )}
                  </>
                ) : "Nessuna ricevuta in archivio. Crea la prima col tasto + sopra."}
              </div>
            ) : (
              <div className="space-y-1.5">
                {items.map((rec) => {
                  const meta = SOURCE_META[rec.source] || SOURCE_META.website;
                  const Icon = meta.icon;
                  const cust = rec.customer || {};
                  const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || "Senza nome";
                  return (
                    <div
                      key={rec.id || rec.number}
                      className={`flex flex-wrap sm:flex-nowrap items-center gap-2 border rounded px-3 py-2 text-xs transition-colors ${
                        rec.voided
                          ? "bg-rose-50/50 border-rose-200 opacity-75"
                          : "bg-white border-[#E8E2D9] hover:border-[#AC5D45]/40"
                      }`}
                      data-testid={`rec-row-${rec.number}`}
                    >
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium shrink-0 ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <div className="flex-1 min-w-0 basis-full sm:basis-auto order-2 sm:order-none w-full sm:w-auto">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-mono font-semibold ${rec.voided ? "text-rose-700 line-through" : "text-[#1E3A5F]"}`}>
                            {rec.number}
                          </span>
                          <span className={`text-[#2D2926] truncate ${rec.voided ? "line-through" : ""}`}>{name}</span>
                          {rec.booking_code && (
                            <span className="text-[10px] text-[#635F59]">· {rec.booking_code}</span>
                          )}
                          {rec.voided && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] bg-rose-600 text-white px-1.5 py-0.5 rounded font-bold"
                              title={`Annullata il ${(rec.voided_at || "").slice(0, 10)}${rec.voided_by ? ` da ${rec.voided_by}` : ""}${rec.voided_reason ? ` — ${rec.voided_reason}` : ""}`}
                            >
                              <Ban className="w-3 h-3" /> ANNULLATA
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#635F59] flex flex-wrap gap-x-2 gap-y-0.5">
                          <span>{(rec.issued_at || "").slice(0, 10)}</span>
                          {cust.email && <span>{cust.email}</span>}
                          {cust.phone && <span className="text-emerald-700">{cust.phone}</span>}
                          {rec.room_name && <span>· {rec.room_name}</span>}
                          {rec.nights ? <span>· {rec.nights} notte/i</span> : null}
                          {rec.last_emailed_at && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 font-medium"
                              title={`Email inviata a ${rec.last_emailed_to || "—"}${rec.last_emailed_by ? ` da ${rec.last_emailed_by}` : ""}`}
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              Inviata {(rec.last_emailed_at || "").slice(0, 10)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="font-bold text-[#AC5D45] shrink-0 ml-auto sm:ml-0">€ {Number(rec.total || 0).toFixed(2)}</div>
                      {cust.email ? (
                        <button
                          onClick={() => emailReceipt(rec)}
                          className={`inline-flex items-center gap-1 h-7 px-2 rounded font-medium text-xs ${
                            rec.last_emailed_at
                              ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300"
                              : "bg-[#1E3A5F] hover:bg-[#152B47] text-white"
                          }`}
                          title={
                            rec.last_emailed_at
                              ? `Inviata il ${(rec.last_emailed_at || "").slice(0, 10)} a ${rec.last_emailed_to || cust.email}. Clicca per reinviare.`
                              : `Invia ricevuta a ${cust.email}`
                          }
                          data-testid={`rec-email-${rec.number}`}
                        >
                          {rec.last_emailed_at ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                          <span className="hidden sm:inline">
                            {rec.last_emailed_at ? "Reinvia" : "Email"}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => emailReceipt(rec)}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded bg-[#FDF1E6] hover:bg-[#FBE5D0] text-[#AC5D45] border border-[#AC5D45]/30 font-medium text-xs"
                          title="Inserisci un indirizzo email per inviare la ricevuta"
                          data-testid={`rec-email-prompt-${rec.number}`}
                        >
                          <Mail className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Email…</span>
                        </button>
                      )}
                      {cust.phone ? (
                        <button
                          onClick={() => sendWhatsAppWithPdf(rec)}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded bg-[#25D366] hover:bg-[#1FBA56] text-white font-medium text-xs"
                          title={`Invia ricevuta + link PDF a ${name} su WhatsApp`}
                          data-testid={`rec-wa-${rec.number}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">WhatsApp</span>
                        </button>
                      ) : (
                        <span
                          className="inline-flex items-center h-7 px-2 rounded bg-[#E8E2D9] text-[#9b948a] text-[10px] italic cursor-help"
                          title="Salva il numero di telefono del cliente per inviare via WhatsApp"
                        >
                          no tel.
                        </span>
                      )}
                      <button
                        onClick={() => viewPdf(rec)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#FAF7F2]"
                        title="Visualizza ricevuta (anteprima PDF in nuova scheda)"
                        data-testid={`rec-view-${rec.number}`}
                      >
                        <Eye className="w-3.5 h-3.5 text-[#1E3A5F]" />
                      </button>
                      <button
                        onClick={() => downloadPdf(rec)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#FAF7F2]"
                        title="Scarica PDF"
                        data-testid={`rec-download-${rec.number}`}
                      >
                        <Download className="w-3.5 h-3.5 text-[#1E3A5F]" />
                      </button>
                      {!rec.voided && (
                        <button
                          onClick={() => editReceipt(rec)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#FAF7F2]"
                          title="Modifica importo / note / metodo pagamento"
                          data-testid={`rec-edit-${rec.number}`}
                        >
                          <Edit3 className="w-3.5 h-3.5 text-[#1E3A5F]" />
                        </button>
                      )}
                      {rec.voided ? (
                        <button
                          onClick={() => restoreReceipt(rec)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-emerald-50"
                          title="Ripristina ricevuta annullata"
                          data-testid={`rec-restore-${rec.number}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5 text-emerald-700" />
                        </button>
                      ) : (
                        <button
                          onClick={() => voidReceipt(rec)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-amber-50"
                          title="Annulla ricevuta (lascia traccia con motivo)"
                          data-testid={`rec-void-${rec.number}`}
                        >
                          <Ban className="w-3.5 h-3.5 text-amber-700" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteReceipt(rec)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50"
                        title="Elimina definitivamente (sconsigliato — usa Annulla per audit fiscale)"
                        data-testid={`rec-delete-${rec.number}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-700" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <ManualReceiptDialog
        open={showManualDialog || !!editingReceipt}
        editingReceipt={editingReceipt}
        initialQuery={showManualDialog ? q : ""}
        initialTab={showManualDialog && q ? "bookings" : "customers"}
        onClose={() => { setShowManualDialog(false); setEditingReceipt(null); }}
        onSaved={() => { setShowManualDialog(false); setEditingReceipt(null); loadReceipts(); }}
      />

      <PdfPreviewDialog
        open={!!pdfPreview}
        onClose={closePdfPreview}
        blobUrl={pdfPreview?.url}
        filename={pdfPreview?.filename}
      />
    </div>
  );
};

// ============================================================
// Manual receipt dialog (search customer or booking → autofill)
// ============================================================
const blankCustomer = { first_name: "", last_name: "", email: "", phone: "", country: "", city: "", document_number: "", tax_code: "", vat_number: "", business_name: "" };
const blankItem = () => ({ description: "", qty: 1, unit_price: 0 });

const ManualReceiptDialog = ({ open, onClose, onSaved, editingReceipt = null, initialQuery = "", initialTab = "customers" }) => {
  const isEdit = !!editingReceipt;
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchTab, setSearchTab] = useState("customers"); // "customers" | "bookings"
  const [saving, setSaving] = useState(false);
  // iter167: track selected booking to show a "loaded" banner in the modal so
  // the user has strong visual confirmation the auto-fill happened (tablet
  // taps sometimes feel unresponsive).
  const [pickedCode, setPickedCode] = useState("");
  // iter167: force-open the "Codice Fiscale / P.IVA / Ragione sociale" section
  // when the guest is a business (no first/last name but has business_name).
  const [taxDetailsOpen, setTaxDetailsOpen] = useState(false);
  const searchInputRef = useRef(null);

  const [form, setForm] = useState({
    customer: { ...blankCustomer },
    booking_code: "",
    check_in: "",
    check_out: "",
    nights: 1,
    room_name: "",
    items: [{ description: "Soggiorno", qty: 1, unit_price: 0 }],
    tourist_tax: 0,
    payment_method: "cash",
    // iter168: multi-payments — array of {date, method, amount, reference}.
    // Legacy single `payment_method` above is kept in sync (= method of last row)
    // for backward compatibility with dashboards / CSV export.
    payments: [],
    notes: "",
  });

  // Load customers + bookings once when dialog opens; pre-fill if editing
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, b] = await Promise.all([
          api.get("/admin/customers").catch(() => ({ data: { customers: [] } })),
          api.get("/admin/bookings").catch(() => ({ data: { bookings: [] } })),
        ]);
        setCustomers(c.data?.customers || []);
        setBookings(b.data?.bookings || b.data || []);
      } catch {
        setCustomers([]); setBookings([]);
      }
    })();

    if (isEdit && editingReceipt) {
      // Pre-fill the entire form with the existing receipt values
      const r = editingReceipt;
      const cust = r.customer || {};
      setForm({
        customer: {
          first_name: cust.first_name || "",
          last_name: cust.last_name || "",
          email: cust.email || "",
          phone: cust.phone || "",
          country: cust.country || "",
          city: cust.city || "",
          document_number: cust.document_number || "",
          tax_code: cust.tax_code || "",
          vat_number: cust.vat_number || "",
          business_name: cust.business_name || "",
        },
        booking_code: r.booking_code || "",
        check_in: r.stay_check_in || r.check_in || "",
        check_out: r.stay_check_out || r.check_out || "",
        nights: r.nights || 1,
        room_name: typeof r.room_name === "string" ? r.room_name : (r.room_name?.it || ""),
        items: (r.items && r.items.length)
          ? r.items.map((it) => ({
              description: it.description || it.label || "Voce",
              qty: Number(it.qty || it.quantity || 1),
              unit_price: Number(it.unit_price || it.price || 0),
            }))
          : [{ description: "Soggiorno", qty: 1, unit_price: Number(r.total || 0) }],
        tourist_tax: Number(r.tourist_tax || 0),
        payment_method: r.payment_method || "cash",
        payments: Array.isArray(r.payments) ? r.payments.map((p) => ({
          date: p.date || "",
          method: p.method || "cash",
          amount: Number(p.amount || 0),
          reference: p.reference || "",
        })) : [],
        notes: r.notes || "",
      });
    } else {
      setForm({
        customer: { ...blankCustomer },
        booking_code: "",
        check_in: "",
        check_out: "",
        nights: 1,
        room_name: "",
        items: [{ description: "Soggiorno", qty: 1, unit_price: 0 }],
        tourist_tax: 0,
        payment_method: "cash",
        payments: [],
        notes: "",
      });
    }
    setSearchQ(initialQuery || "");
    setSearchTab(initialTab || "customers");
    setPickedCode("");
    setTaxDetailsOpen(false);
  }, [open, isEdit, editingReceipt, initialQuery, initialTab]);

  const filteredCustomers = useMemo(() => {
    if (!searchQ) return customers.slice(0, 30);
    const lc = searchQ.toLowerCase();
    return customers
      .filter((c) =>
        (c.first_name || "").toLowerCase().includes(lc) ||
        (c.last_name || "").toLowerCase().includes(lc) ||
        (c.email || "").toLowerCase().includes(lc) ||
        (c.phone || "").toLowerCase().includes(lc),
      )
      .slice(0, 30);
  }, [customers, searchQ]);

  const filteredBookings = useMemo(() => {
    if (!searchQ) return bookings.slice(0, 30);
    const lc = searchQ.toLowerCase();
    return bookings
      .filter((b) => {
        const g = b.guest || {};
        const rn = typeof b.room_name === "string"
          ? b.room_name
          : (b.room_name?.it || "");
        return (
          (b.code || "").toLowerCase().includes(lc) ||
          (b.external_id || "").toString().toLowerCase().includes(lc) ||
          (g.first_name || "").toLowerCase().includes(lc) ||
          (g.last_name || "").toLowerCase().includes(lc) ||
          (g.email || "").toLowerCase().includes(lc) ||
          (g.phone || "").toLowerCase().includes(lc) ||
          rn.toLowerCase().includes(lc)
        );
      })
      .slice(0, 30);
  }, [bookings, searchQ]);

  const pickCustomer = (c) => {
    // First, populate guest details
    setForm((f) => ({
      ...f,
      customer: {
        first_name: c.first_name || "",
        last_name: c.last_name || "",
        email: c.email || "",
        phone: c.phone || "",
        country: c.country || c.nationality || "",
        city: c.city || "",
        document_number: c.document_number || "",
        tax_code: c.tax_code || "",
        vat_number: c.vat_number || "",
        business_name: c.business_name || "",
      },
    }));

    // Then look up this customer's MOST RECENT booking (by email primarily, phone fallback)
    // and autofill check-in/check-out, room, total, etc — turning a customer click into a
    // ready-to-print receipt in one tap.
    const custEmail = (c.email || "").toLowerCase().trim();
    const custPhone = (c.phone || "").replace(/\s+/g, "");
    const matches = bookings.filter((b) => {
      const g = b.guest || {};
      const gEmail = (g.email || "").toLowerCase().trim();
      const gPhone = (g.phone || "").replace(/\s+/g, "");
      if (custEmail && gEmail && gEmail === custEmail) return true;
      if (custPhone && gPhone && gPhone === custPhone) return true;
      return false;
    });
    if (matches.length === 0) return;

    // Pick the most recent: prefer "active" statuses, fallback to highest check_in
    const score = (b) => {
      const statusRank = ({ checked_in: 5, completed: 4, confirmed: 3, pending: 2, cancelled: 1 }[b.status] || 0);
      const dateRank = b.check_in ? new Date(b.check_in).getTime() : 0;
      return statusRank * 1e13 + dateRank;
    };
    const latest = matches.slice().sort((a, b) => score(b) - score(a))[0];
    const roomName = typeof latest.room_name === "string"
      ? latest.room_name
      : (latest.room_name?.it || "Camera");

    setForm((f) => ({
      ...f,
      booking_code: latest.code || "",
      check_in: latest.check_in || "",
      check_out: latest.check_out || "",
      nights: latest.nights || 1,
      room_name: roomName,
      items: [{
        description: `Soggiorno · ${roomName} · ${latest.nights || 1} notte/i`,
        qty: 1,
        unit_price: Number(latest.total || 0),
      }],
      tourist_tax: Number(latest.tourist_tax || 0),
      payment_method: latest.payment_method || "cash",
    }));
    setPickedCode(latest.code || "");

    toast.success(
      matches.length === 1
        ? `Compilata con la prenotazione ${latest.code}`
        : `Compilata con la più recente di ${matches.length}: ${latest.code}`,
      { duration: 4000 },
    );
  };

  const pickBooking = (b) => {
    // iter167: dismiss the virtual keyboard first — on tablet a first tap
    // sometimes just blurs the search input without registering the click on
    // the row, giving the impression that "nothing happened".
    try { searchInputRef.current?.blur?.(); } catch { /* noop */ }
    const g = b.guest || {};
    // If the booking record has an empty guest (typical of Booking.com iCal
    // skeletons), try to enrich from the customers rubric using the booking
    // code or email so we never present the user with a "blank" form.
    let fallback = {};
    const hasGuestName = !!((g.first_name || "").trim() || (g.last_name || "").trim());
    if (!hasGuestName) {
      const code = (b.code || "").toLowerCase();
      const email = (g.email || "").toLowerCase().trim();
      fallback = customers.find((c) => {
        if (email && (c.email || "").toLowerCase() === email) return true;
        if (code && (c.last_booking_code || "").toLowerCase() === code) return true;
        return false;
      }) || {};
    }
    // iter167: business heuristic — a "guest first_name" like "Mi.Med Srl"
    // (or containing legal suffixes) really means the booking is for a
    // company. Move that into business_name so the receipt shows a proper
    // "Ragione sociale" and the "Nome/Cognome" fields stay clean.
    const BIZ_SUFFIX = /\b(s\.?r\.?l\.?|s\.?p\.?a\.?|s\.?a\.?s\.?|s\.?n\.?c\.?|snc|srl|spa|sas|ltd|gmbh|inc|s\.?r\.?l\.?s\.?|s\.?c\.?a\.?r\.?l\.?)\b/i;
    let inferredBusiness = g.business_name || fallback.business_name || "";
    let personFirst = g.first_name || fallback.first_name || "";
    let personLast = g.last_name || fallback.last_name || "";
    const combinedName = `${personFirst} ${personLast}`.trim();
    if (!inferredBusiness && combinedName && BIZ_SUFFIX.test(combinedName)) {
      inferredBusiness = combinedName;
      personFirst = "";
      personLast = "";
    }
    const merged = {
      first_name: personFirst,
      last_name: personLast,
      email: g.email || fallback.email || "",
      phone: g.phone || fallback.phone || "",
      country: g.country || g.nationality || fallback.country || fallback.nationality || "",
      city: g.city || fallback.city || "",
      document_number: g.document_number || fallback.document_number || "",
      tax_code: g.tax_code || fallback.tax_code || "",
      vat_number: g.vat_number || fallback.vat_number || "",
      business_name: inferredBusiness,
    };
    const rn = typeof b.room_name === "string" ? b.room_name : (b.room_name?.it || "Camera");
    setForm((f) => {
      // iter168: if the booking has a recorded deposit (acconto pagato in
      // anticipo), pre-fill the first payment row so the receipt already
      // shows "Acconto · <metodo> · € XX" and the host just adds the "Saldo"
      // row when the guest pays the rest at check-in.
      const dep = Number(b.deposit_amount || 0);
      const seedPayments = [];
      if (dep > 0) {
        const rawDate = b.deposit_paid_at || b.created_at || "";
        const isoDate = typeof rawDate === "string" && rawDate.length >= 10 ? rawDate.slice(0, 10) : "";
        seedPayments.push({
          date: isoDate,
          method: b.deposit_method || b.payment_method || "transfer",
          amount: dep,
          reference: "",
        });
      }
      return {
      ...f,
      // Only overwrite customer fields that we have data for — preserves any
      // info the user may have already typed when the booking is a skeleton.
      customer: {
        ...f.customer,
        ...Object.fromEntries(Object.entries(merged).filter(([, v]) => v)),
      },
      booking_code: b.code || "",
      check_in: b.check_in || "",
      check_out: b.check_out || "",
      nights: b.nights || 1,
      room_name: rn,
      items: [{
        description: `Soggiorno · ${rn} · ${b.nights || 1} notte/i`,
        qty: 1,
        unit_price: Number(b.total || 0),
      }],
      tourist_tax: Number(b.tourist_tax || 0),
      payment_method: b.payment_method || "cash",
      payments: seedPayments,
      };
    });
    // iter167: strong visible confirmation + expose Ragione sociale if we
    // detected a business (so the user sees the auto-filled value).
    setPickedCode(b.code || "");
    if (merged.business_name || merged.tax_code || merged.vat_number) {
      setTaxDetailsOpen(true);
    }
    const name = merged.business_name || `${merged.first_name} ${merged.last_name}`.trim();
    toast.success(
      name
        ? `Prenotazione ${b.code || ""} di ${name} caricata`
        : `Prenotazione ${b.code || ""} caricata · completa i dati cliente`,
      { duration: 4000 },
    );
  };

  const updateItem = (i, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: value };
      return { ...f, items };
    });
  };

  const subtotal = form.items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const total = subtotal + Number(form.tourist_tax || 0);

  const save = async () => {
    if (!form.customer.first_name && !form.customer.last_name) {
      return toast.error("Inserisci il nome del cliente");
    }
    if (subtotal <= 0) {
      return toast.error("Inserisci almeno una voce con importo");
    }
    setSaving(true);
    try {
      const payload = {
        customer: form.customer,
        booking_code: form.booking_code || undefined,
        check_in: form.check_in || undefined,
        check_out: form.check_out || undefined,
        nights: form.nights || undefined,
        room_name: form.room_name || undefined,
        items: form.items.map((it) => ({
          description: it.description,
          qty: Number(it.qty || 1),
          unit_price: Number(it.unit_price || 0),
          total: Number(it.qty || 1) * Number(it.unit_price || 0),
        })),
        tourist_tax: Number(form.tourist_tax || 0),
        payment_method: form.payment_method,
        // iter168: multi-payments — send full list; keep `payment_method` in
        // sync (= method of the last row) for legacy dashboards + CSV export.
        payments: (form.payments || []).map((p) => ({
          date: p.date || "",
          method: p.method || "cash",
          amount: Number(p.amount || 0),
          reference: (p.reference || "").trim(),
        })).filter((p) => p.amount > 0 || p.date || p.reference),
        notes: form.notes || undefined,
      };
      // Keep legacy single field aligned with the last recorded payment.
      if (payload.payments.length > 0) {
        payload.payment_method = payload.payments[payload.payments.length - 1].method;
      }
      if (isEdit) {
        // Flatten customer payload because the PUT endpoint accepts top-level fields,
        // not a nested `customer` object (matches _UpdateReceiptPayload server side)
        const editPayload = {
          ...form.customer,
          booking_code: form.booking_code || undefined,
          nights: form.nights || undefined,
          room_name: form.room_name || undefined,
          items: payload.items,
          subtotal: payload.items.reduce((s, it) => s + it.total, 0),
          tourist_tax: payload.tourist_tax,
          total: payload.items.reduce((s, it) => s + it.total, 0) + payload.tourist_tax,
          payment_method: payload.payment_method,
          payments: payload.payments,
          notes: form.notes,
        };
        await api.put(`/admin/receipts/${encodeURIComponent(editingReceipt.number)}`, editPayload);
        toast.success(`✓ Ricevuta ${editingReceipt.number} aggiornata`);
        onSaved && onSaved();
        return;
      }
      const r = await api.post("/admin/receipts/manual", payload);
      toast.success(`Ricevuta ${r.data.number} creata`);
      // Auto-download PDF
      try {
        const pdf = await api.get(`/admin/receipts/${encodeURIComponent(r.data.number)}/pdf`, { responseType: "blob" });
        saveBlob(pdf.data, `ricevuta_${r.data.number}.pdf`);
      } catch { /* non blocking */ }
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e, isEdit ? "Errore aggiornamento ricevuta" : "Errore creazione ricevuta"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-receipt-dialog" : "manual-receipt-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1E3A5F]">
            <ReceiptText className="w-5 h-5 text-[#AC5D45]" />
            {isEdit ? `Modifica ricevuta ${editingReceipt?.number || ""}` : "Nuova ricevuta manuale"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search section */}
          <div className="bg-[#FAF7F2] border border-[#E8E2D9] rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setSearchTab("customers")}
                className={`px-2.5 py-1 rounded text-xs font-medium ${searchTab === "customers" ? "bg-[#AC5D45] text-white" : "bg-white border border-[#E8E2D9] text-[#635F59]"}`}
                data-testid="search-tab-customers"
              >
                <User className="w-3 h-3 inline mr-1" /> Rubrica clienti
              </button>
              <button
                type="button"
                onClick={() => setSearchTab("bookings")}
                className={`px-2.5 py-1 rounded text-xs font-medium ${searchTab === "bookings" ? "bg-[#AC5D45] text-white" : "bg-white border border-[#E8E2D9] text-[#635F59]"}`}
                data-testid="search-tab-bookings"
              >
                <BookOpen className="w-3 h-3 inline mr-1" /> Archivio prenotazioni
              </button>
              <div className="flex-1 relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9b948a]" />
                <Input
                  ref={searchInputRef}
                  className="pl-7 h-8 text-xs"
                  placeholder="Cerca nome, email, codice…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  data-testid="manual-search-input"
                />
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {searchTab === "customers" ? (
                filteredCustomers.length === 0 ? (
                  <p className="text-[11px] text-[#9b948a] italic px-1">Nessun cliente.</p>
                ) : (
                  filteredCustomers.map((c) => (
                    <button
                      type="button"
                      key={c.id || c.email}
                      onPointerDown={() => { try { searchInputRef.current?.blur?.(); } catch { /* noop */ } }}
                      onClick={() => pickCustomer(c)}
                      className="w-full text-left bg-white hover:bg-[#FFF8F0] active:bg-[#FFEBD8] border border-[#E8E2D9] hover:border-[#AC5D45] rounded px-2 py-2.5 text-xs flex items-center justify-between gap-2"
                      data-testid={`pick-customer-${c.email}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#1E3A5F] truncate">{c.first_name} {c.last_name}</div>
                        <div className="text-[10px] text-[#635F59] truncate">{c.email} {c.phone ? `· ${c.phone}` : ""}</div>
                      </div>
                      <div className="text-[10px] text-[#635F59] shrink-0">{c.total_bookings || 0} prenot.</div>
                      <ChevronRight className="w-4 h-4 text-[#AC5D45] shrink-0" />
                    </button>
                  ))
                )
              ) : (
                filteredBookings.length === 0 ? (
                  <p className="text-[11px] text-[#9b948a] italic px-1">Nessuna prenotazione.</p>
                ) : (
                  filteredBookings.map((b) => {
                    const g = b.guest || {};
                    const rn = typeof b.room_name === "string" ? b.room_name : (b.room_name?.it || "");
                    return (
                      <button
                        type="button"
                        key={b.id || b.code}
                        onPointerDown={() => { try { searchInputRef.current?.blur?.(); } catch { /* noop */ } }}
                        onClick={() => pickBooking(b)}
                        className="w-full text-left bg-white hover:bg-[#FFF8F0] active:bg-[#FFEBD8] border border-[#E8E2D9] hover:border-[#AC5D45] rounded px-2 py-2.5 text-xs flex items-center justify-between gap-2"
                        data-testid={`pick-booking-${b.code}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#1E3A5F] truncate">
                            <span className="font-mono">{b.code}</span> · {g.first_name} {g.last_name}
                          </div>
                          <div className="text-[10px] text-[#635F59] truncate">{formatDate(b.check_in)} → {formatDate(b.check_out)} · {rn}</div>
                        </div>
                        <div className="text-[10px] font-bold text-[#AC5D45] shrink-0">€ {Number(b.total || 0).toFixed(2)}</div>
                        <ChevronRight className="w-4 h-4 text-[#AC5D45] shrink-0" />
                      </button>
                    );
                  })
                )
              )}
            </div>
            <p className="text-[10px] text-[#9b948a] mt-1 italic">Tocca una riga per compilare automaticamente i campi qui sotto.</p>
          </div>

          {/* iter167: strong visible confirmation that a booking/customer was picked */}
          {pickedCode && (
            <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="picked-confirmation">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>Prenotazione <strong className="font-mono">{pickedCode}</strong> caricata — controlla i campi sotto e completa se necessario.</span>
            </div>
          )}

          {/* Customer fields */}
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.customer.first_name} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, first_name: e.target.value } }))} data-testid="m-first-name" />
            </div>
            <div>
              <Label className="text-xs">Cognome</Label>
              <Input value={form.customer.last_name} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, last_name: e.target.value } }))} data-testid="m-last-name" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.customer.email} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, email: e.target.value } }))} data-testid="m-email" />
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={form.customer.phone} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, phone: e.target.value } }))} data-testid="m-phone" />
            </div>
            <div>
              <Label className="text-xs">Città</Label>
              <Input value={form.customer.city} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, city: e.target.value } }))} />
            </div>
            <div>
              <Label className="text-xs">Paese</Label>
              <Input value={form.customer.country} onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, country: e.target.value } }))} />
            </div>
          </div>

          {/* Tax / fattura ditta — opzionali */}
          <details
            className="bg-[#FAF7F2] border border-[#E8E2D9] rounded p-2.5"
            open={taxDetailsOpen}
            onToggle={(e) => setTaxDetailsOpen(e.currentTarget.open)}
          >
            <summary className="cursor-pointer text-xs font-semibold text-[#1E3A5F]">Codice Fiscale / P.IVA / Ragione sociale (opzionale)</summary>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              <div className="sm:col-span-2">
                <Label className="text-xs">Ragione sociale (se ditta)</Label>
                <Input
                  value={form.customer.business_name}
                  onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, business_name: e.target.value } }))}
                  placeholder="Es. ACME S.r.l."
                  data-testid="m-business-name"
                />
              </div>
              <div>
                <Label className="text-xs">Codice Fiscale</Label>
                <Input
                  value={form.customer.tax_code}
                  onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, tax_code: e.target.value.toUpperCase() } }))}
                  placeholder="RSSMRA80A01H501Z"
                  maxLength={16}
                  data-testid="m-tax-code"
                />
              </div>
              <div>
                <Label className="text-xs">Partita IVA</Label>
                <Input
                  value={form.customer.vat_number}
                  onChange={(e) => setForm((f) => ({ ...f, customer: { ...f.customer, vat_number: e.target.value.replace(/\D/g, "") } }))}
                  placeholder="12345678901"
                  maxLength={11}
                  data-testid="m-vat-number"
                />
              </div>
            </div>
          </details>

          {/* Stay info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Codice prenotazione</Label>
              <Input value={form.booking_code} onChange={(e) => setForm({ ...form, booking_code: e.target.value })} data-testid="m-booking-code" />
            </div>
            <div>
              <Label className="text-xs">Arrivo</Label>
              <DateInputIT value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Partenza</Label>
              <DateInputIT value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Notti</Label>
              <Input type="number" min="1" value={form.nights} onChange={(e) => setForm({ ...form, nights: parseInt(e.target.value, 10) || 1 })} />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <Label className="text-xs">Camera</Label>
              <Input value={form.room_name} onChange={(e) => setForm({ ...form, room_name: e.target.value })} />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-[#635F59]">Voci ricevuta</Label>
              <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }))} data-testid="m-add-item">
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi voce
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={it._uid || `qi-${i}`} className="grid grid-cols-12 gap-2 bg-[#FAF7F2] border border-[#E8E2D9] rounded p-2">
                  <div className="col-span-12 sm:col-span-6">
                    <Input placeholder="Descrizione" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`m-item-desc-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Input type="number" min="1" value={it.qty} onChange={(e) => updateItem(i, "qty", parseInt(e.target.value, 10) || 0)} data-testid={`m-item-qty-${i}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)} data-testid={`m-item-price-${i}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex items-center justify-end">
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50" title="Rimuovi">
                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax + payment + notes + totals */}
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Tassa di soggiorno (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.tourist_tax} onChange={(e) => setForm({ ...form, tourist_tax: parseFloat(e.target.value) || 0 })} data-testid="m-tourist-tax" />
            </div>
            <div className="sm:col-span-2 flex flex-col justify-end items-end font-semibold text-[#1E3A5F]">
              <div className="text-xs text-[#635F59]">Subtotale € {subtotal.toFixed(2)}</div>
              <div className="text-lg text-[#AC5D45]" data-testid="m-total">TOTALE € {total.toFixed(2)}</div>
            </div>
          </div>

          {/* iter168: Multi-payments editor (acconto + saldo + ...) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-[#635F59]">Pagamenti ricevuti</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setForm((f) => ({
                  ...f,
                  payments: [...(f.payments || []), {
                    date: new Date().toISOString().slice(0, 10),
                    method: "cash",
                    amount: 0,
                    reference: "",
                  }],
                }))}
                data-testid="m-add-payment"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi pagamento
              </Button>
            </div>
            {(form.payments || []).length === 0 ? (
              <p className="text-[11px] text-[#9b948a] italic px-1">
                Nessun pagamento registrato. Clicca "+ Aggiungi pagamento" per acconto, saldo o pagamento unico.
              </p>
            ) : (
              <div className="space-y-2">
                {form.payments.map((p, i) => (
                  <div key={`pmt-${i}`} className="grid grid-cols-12 gap-2 bg-[#FAF7F2] border border-[#E8E2D9] rounded p-2 items-end" data-testid={`m-payment-row-${i}`}>
                    <div className="col-span-6 sm:col-span-3">
                      <Label className="text-[10px] text-[#635F59]">Data</Label>
                      <DateInputIT
                        value={p.date}
                        onChange={(e) => setForm((f) => {
                          const arr = [...f.payments];
                          arr[i] = { ...arr[i], date: e.target.value };
                          return { ...f, payments: arr };
                        })}
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <Label className="text-[10px] text-[#635F59]">Metodo</Label>
                      <Select
                        value={p.method}
                        onValueChange={(v) => setForm((f) => {
                          const arr = [...f.payments];
                          arr[i] = { ...arr[i], method: v };
                          return { ...f, payments: arr };
                        })}
                      >
                        <SelectTrigger data-testid={`m-payment-method-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Contanti</SelectItem>
                          <SelectItem value="card">Carta</SelectItem>
                          <SelectItem value="transfer">Bonifico</SelectItem>
                          <SelectItem value="paypal">PayPal</SelectItem>
                          <SelectItem value="satispay">Satispay</SelectItem>
                          <SelectItem value="postepay">Postepay</SelectItem>
                          <SelectItem value="online">Pagamento online</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-8 sm:col-span-4">
                      <Label className="text-[10px] text-[#635F59]">
                        Riferimento {(p.method === "transfer" || p.method === "bonifico") ? "(N° bonifico)" : "(opzionale)"}
                      </Label>
                      <Input
                        placeholder={(p.method === "transfer" || p.method === "bonifico") ? "es. TRN 123456789" : "es. n° transazione"}
                        value={p.reference}
                        onChange={(e) => setForm((f) => {
                          const arr = [...f.payments];
                          arr[i] = { ...arr[i], reference: e.target.value };
                          return { ...f, payments: arr };
                        })}
                        data-testid={`m-payment-ref-${i}`}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Label className="text-[10px] text-[#635F59]">Importo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={p.amount}
                        onChange={(e) => setForm((f) => {
                          const arr = [...f.payments];
                          arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                          return { ...f, payments: arr };
                        })}
                        data-testid={`m-payment-amount-${i}`}
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, payments: f.payments.filter((_, idx) => idx !== i) }))}
                        className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50"
                        title="Rimuovi pagamento"
                        data-testid={`m-remove-payment-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-xs text-[#635F59] pr-1" data-testid="m-payments-total">
                  Totale pagato: <strong className="text-[#1E3A5F]">€ {form.payments.reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2)}</strong>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Note (opzionali)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="m-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white" data-testid="m-save">
            {saving
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvo…</>
              : isEdit
                ? <><Edit3 className="w-4 h-4 mr-2" /> Salva modifiche</>
                : <><Download className="w-4 h-4 mr-2" /> Crea e scarica PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// SECTION 2: Preventivi (mini builder + archive)
// Layout: simile a "Nuova prenotazione" admin. UN solo piano tariffario,
// acconto/saldo calcolati automaticamente dal cancellation_type del piano.
// ============================================================
// Calcola l'età di un preventivo in ore a partire da `generated_at`.
// Ritorna { hours, label, tone } dove:
//   tone: "fresh" (<48h) | "stale" (48h..7g) | "expired" (>7g)
//   label: stringa compatta italiana, es. "3g fa", "20h fa", "12g · scaduto"
const quoteAgeInfo = (generatedAt) => {
  if (!generatedAt) return null;
  const t = new Date(generatedAt).getTime();
  if (!Number.isFinite(t)) return null;
  const diffH = (Date.now() - t) / 36e5;
  const days = Math.floor(diffH / 24);
  const hours = Math.floor(diffH);
  let tone = "fresh";
  if (diffH > 7 * 24) tone = "expired";
  else if (diffH >= 48) tone = "stale";
  let label;
  if (tone === "expired") label = `${days}g · scaduto`;
  else if (days >= 1) label = `${days}g fa`;
  else if (hours >= 1) label = `${hours}h fa`;
  else label = "adesso";
  return { hours: diffH, tone, label };
};

const QuotesSection = () => {
  // iter173: collapse toggle for the entire Preventivi panel (host request:
  // "quando chiudo Ricevute vorrei che chiudessi anche i Preventivi che
  // sotto rimane sempre aperto"). Default: aperto per non alterare il
  // comportamento agli utenti esistenti; l'host può richiuderlo.
  const [panelOpen, setPanelOpen] = useState(true);
  const [open, setOpen] = useState(false);
  const [archive, setArchive] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveOnlyConfirmed, setArchiveOnlyConfirmed] = useState(false);
  // Filtro età preventivo: "all" | "stale" (≥48h, follow-up) | "expired" (>7g, scaduto)
  const [archiveAge, setArchiveAge] = useState("all");
  const [building, setBuilding] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename } | null
  // Lista dinamica dei piani tariffari (Standard / Flessibile / Non rimb. / Mensile)
  // configurati in /admin/rate-plans. L'host sceglie quali includere nel PDF
  // come tabella di confronto (default: tutti i piani attivi pre-selezionati).
  const [ratePlans, setRatePlans] = useState([]);
  const [selectedRatePlans, setSelectedRatePlans] = useState([]);
  const [roomsCatalog, setRoomsCatalog] = useState([]);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    arrival: "",
    departure: "", // Data partenza opzionale — se compilata insieme ad arrival, calcola notti totali automaticamente
    // === MULTI-CAMERA ===
    // Ogni voce rappresenta UNA tipologia di camera nel preventivo:
    //   { _uid, room_id, nights, guests, custom_nightly_rate }
    // Le camere sono IN PARALLELO (stesso soggiorno, camere diverse) —
    // NON in serie. Quindi il totale notti del soggiorno = MAX(notti per camera),
    // non la somma.
    rooms: [{ _uid: `room-${Date.now()}`, room_id: "", nights: "", guests: "", custom_nightly_rate: "" }],
    custom_total: "",
    // Sconto % opzionale (0-100). Si applica al subtotale camere prima dei
    // costi extra. Inviato al backend come `plan_discount_pct = -discount_pct`
    // (segno negativo perché il backend tratta il discount_pct come
    // "maggiorazione" se positivo).
    discount_pct: "",
    // Tipo sconto: "percent" (default, retro-compatibile) oppure "euro"
    // (sconto in valore assoluto in €). L'host può scegliere dal menu a
    // tendina accanto al campo. Lo sconto si applica al subtotale camere
    // O al forfait se impostato — in entrambi i casi PRIMA degli extra.
    discount_type: "percent",
    notes: "",
    items: [],
  });
  // ID preventivo in modifica (quando l'host clicca matita su un archive).
  // Se valorizzato, "Genera preventivo" sostituisce il vecchio doc.
  const [editingQuoteId, setEditingQuoteId] = useState(null);

  // Reset completo del form (usato dopo modifica/genera o per "Annulla modifica").
  const resetForm = () => {
    setForm({
      client_name: "", client_email: "", client_phone: "",
      arrival: "", departure: "",
      rooms: [{ _uid: `room-${Date.now()}`, room_id: "", nights: "", guests: "", custom_nightly_rate: "" }],
      custom_total: "", discount_pct: "", discount_type: "percent", notes: "", items: [],
    });
    setEditingQuoteId(null);
  };

  // Carica un preventivo dall'archive nel form per modifica.
  const loadQuoteForEdit = (q) => {
    const rs = Array.isArray(q.rooms_input) && q.rooms_input.length > 0
      ? q.rooms_input.map((r, i) => ({
          _uid: `room-edit-${Date.now()}-${i}`,
          room_id: r.room_id || "",
          nights: r.nights || "",
          guests: r.guests || "",
          custom_nightly_rate: r.custom_nightly_rate ?? "",
        }))
      : [{ _uid: `room-edit-${Date.now()}`, room_id: q.room_id || "", nights: q.nights || "", guests: q.guests || "", custom_nightly_rate: "" }];
    // Voci extra (escludo Pernottamento + righe Sconto: entrambe vengono
    // costruite automaticamente dal backend. Le righe sconto ricomparivano
    // due volte in edit-mode perché venivano lette dal DB e poi ri-generate
    // dal backend al submit — segnalazione host 04/07/2026).
    const extras = (q.items || [])
      .filter((it) => {
        const d = (it.description || "");
        if (d.startsWith("Pernottamento")) return false;
        // Riga sconto già in DB (label costruita dal backend): il testo può
        // essere "Sconto (-10%)" (percentuale) o "Sconto (-€ N.NN)" (euro).
        // In entrambi i casi lo filtriamo — è ricostruito dal backend usando
        // `plan_discount_pct` / `discount_euro` salvati nella quote.
        if (/^Sconto\s*\(/i.test(d)) return false;
        return true;
      })
      .map((it, i) => ({
        _uid: `extra-edit-${Date.now()}-${i}`,
        description: it.description || "",
        qty: it.qty || 1,
        unit_price: it.unit_price || 0,
      }));
    setForm({
      client_name: q.client_name || "",
      client_email: q.client_email || "",
      client_phone: q.client_phone || "",
      arrival: q.arrival || "",
      departure: q.departure || "",
      rooms: rs,
      custom_total: q.custom_total_input ?? "",
      // L'host può aver salvato un preventivo con sconto applicato — lo
      // recupero da `plan_discount_pct` (segno negativo nel backend).
      discount_pct: q.plan_discount_pct != null
        ? String(Math.abs(Number(q.plan_discount_pct) || 0))
        : (q.discount_euro != null ? String(Math.abs(Number(q.discount_euro) || 0)) : ""),
      discount_type: q.discount_euro != null && Number(q.discount_euro) !== 0 ? "euro" : "percent",
      notes: q.notes || "",
      items: extras,
    });
    setEditingQuoteId(q.id);
    setOpen(true);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.info("Preventivo caricato per modifica. Le modifiche sostituiranno il PDF originale.");
    }, 100);
  };

  // Calcolo automatico notti da arrivo+partenza (utility)
  const computeNightsFromDates = (a, d) => {
    if (!a || !d) return null;
    try {
      const da = new Date(a); const dd = new Date(d);
      if (isNaN(da) || isNaN(dd)) return null;
      const ms = dd.getTime() - da.getTime();
      const n = Math.round(ms / (1000 * 60 * 60 * 24));
      return n > 0 ? n : null;
    } catch { return null; }
  };

  // Quando l'host imposta sia arrival sia departure, aggiorno automaticamente
  // le notti di TUTTE le camere che non hanno ancora un valore notti (così
  // l'host può modificarle singolarmente dopo se serve).
  const setArrivalDeparture = (arrival, departure) => setForm((f) => {
    const computed = computeNightsFromDates(arrival, departure);
    if (computed === null) return { ...f, arrival, departure };
    return {
      ...f, arrival, departure,
      rooms: (f.rooms || []).map((r) => ({ ...r, nights: r.nights === "" || !r.nights ? computed : r.nights })),
    };
  });

  // ===== Gestione camere (multi) =====
  const addRoom = () => setForm((f) => {
    // Notti pre-popolate dal calcolo automatico arrivo+partenza, se presente
    const autoNights = computeNightsFromDates(f.arrival, f.departure);
    return {
      ...f,
      rooms: [...(f.rooms || []), { _uid: `room-${Date.now()}-${Math.random()}`, room_id: "", nights: autoNights || "", guests: "", custom_nightly_rate: "" }],
    };
  });
  // Duplica una camera già inserita: copia tipologia/notti/ospiti/tariffa
  // per velocizzare l'inserimento quando ci sono camere identiche.
  const duplicateRoom = (uid) => setForm((f) => {
    const src = (f.rooms || []).find((r) => r._uid === uid);
    if (!src) return f;
    const copy = { ...src, _uid: `room-${Date.now()}-${Math.random()}` };
    const idx = f.rooms.findIndex((r) => r._uid === uid);
    const next = [...f.rooms];
    next.splice(idx + 1, 0, copy);
    return { ...f, rooms: next };
  });
  const removeRoom = (uid) => setForm((f) => {
    const next = (f.rooms || []).filter((r) => r._uid !== uid);
    return { ...f, rooms: next.length > 0 ? next : [{ _uid: `room-${Date.now()}`, room_id: "", nights: "", guests: "", custom_nightly_rate: "" }] };
  });
  const updateRoom = (uid, patch) => setForm((f) => ({
    ...f,
    rooms: (f.rooms || []).map((r) => r._uid === uid ? { ...r, ...patch } : r),
  }));

  // Camera "principale" = la prima, usata come default per piani/voci aggiuntive
  // (es. "Letto extra" → qty = notti della camera principale).

  // ===== Totale "base" (Standard Rate, nessuno sconto) =====
  // Somma le tariffe di tutte le camere del preventivo.
  // I totali per-piano vengono calcolati nel PDF applicando il discount_pct.
  // L'acconto/saldo sono calcolati AUTOMATICAMENTE in base al cancellation_type
  // di ciascun piano: flexible_* → 30% acconto · non_refundable/monthly → 100%.
  const findRoomById = (rid) => roomsCatalog.find((r) => r.id === rid);
  // Camera principale (la prima del preventivo) — usata per UI default e
  // per calcolare le quantità di voci extra "per notte" / "per ospite".
  const primaryRoom = (form.rooms && form.rooms[0]) || { room_id: "", nights: 1, guests: 2, custom_nightly_rate: "" };
  const selectedRoom = useMemo(
    () => findRoomById(primaryRoom.room_id),
    [roomsCatalog, primaryRoom.room_id]
  );
  const baseRate = Number(selectedRoom?.price_per_night || 0);

  // ===== Tariffe stagionali per camera =====
  // Per ogni camera selezionata, recupero la tariffa media/notte effettiva
  // dal price_calendar (override stagionali — es. Agosto +50%). Così il
  // subtotale visualizzato in UI coincide con quello calcolato dal backend
  // al momento della generazione del PDF.
  // Chiavi: `${room_id}|${arrival}|${nights}` → media €/notte.
  const [seasonalRates, setSeasonalRates] = useState({});

  useEffect(() => {
    const rooms = form.rooms || [];
    if (!form.arrival || rooms.length === 0) return;
    const targets = rooms
      .filter((r) => r.room_id && (r.nights || form.departure))
      .map((r) => {
        const n = Math.max(1, Number(r.nights || computeNightsFromDates(form.arrival, form.departure) || 1));
        return { room_id: r.room_id, nights: n, key: `${r.room_id}|${form.arrival}|${n}` };
      });
    const missing = targets.filter((t) => !(t.key in seasonalRates));
    if (missing.length === 0) return;
    (async () => {
      const updates = {};
      for (const t of missing) {
        try {
          const ci = new Date(form.arrival);
          const co = new Date(ci.getTime() + t.nights * 24 * 60 * 60 * 1000);
          const isoCo = co.toISOString().slice(0, 10);
          const r = await api.get(`/calendar/${t.room_id}`, { params: { from_date: form.arrival, to_date: isoCo } });
          const days = Array.isArray(r.data?.days) ? r.data.days : [];
          if (days.length > 0) {
            const sum = days.reduce((s, d) => s + Number(d.price || 0), 0);
            updates[t.key] = +(sum / days.length).toFixed(2);
          }
        } catch {
          // Lascia non valorizzato: si userà il base rate della camera.
        }
      }
      if (Object.keys(updates).length > 0) {
        setSeasonalRates((prev) => ({ ...prev, ...updates }));
      }
    })();
  }, [form.arrival, form.departure, form.rooms, seasonalRates]);

  // Tariffa effettiva (€/notte) per una riga camera del preventivo.
  // Priorità: custom_nightly_rate (host override) > seasonal (calendar) > base.
  const effectiveNightlyRate = (r) => {
    if (r.custom_nightly_rate !== "" && Number(r.custom_nightly_rate) > 0) {
      return Number(r.custom_nightly_rate);
    }
    const n = Math.max(1, Number(r.nights || computeNightsFromDates(form.arrival, form.departure) || 1));
    const key = `${r.room_id}|${form.arrival}|${n}`;
    if (form.arrival && seasonalRates[key] && seasonalRates[key] > 0) {
      return seasonalRates[key];
    }
    const room = findRoomById(r.room_id);
    return Number(room?.price_per_night || 0);
  };

  // Totale di tutte le camere (somma riga × notti) usando la tariffa effettiva.
  const roomsTotal = useMemo(() => {
    const arr = form.rooms || [];
    return arr.reduce((sum, r) => {
      const rate = effectiveNightlyRate(r);
      const n = Math.max(1, Number(r.nights || 1));
      return sum + rate * n;
    }, 0);
  }, [form.rooms, roomsCatalog, seasonalRates, form.arrival, form.departure]);

  // Calcolo dello sconto: può essere in % o in € (valore assoluto).
  // Applicato al SALDO TOTALE del preventivo (camere + extras).
  const computeDiscountAmount = (subtotal) => {
    const raw = Number(form.discount_pct || 0);
    if (!raw || isNaN(raw) || raw <= 0) return 0;
    if (form.discount_type === "euro") {
      return Math.min(subtotal, raw);
    }
    return +(subtotal * Math.max(0, Math.min(100, raw)) / 100).toFixed(2);
  };

  const total = useMemo(() => {
    // Subtotale camere: forfait se impostato, altrimenti somma camera×notti.
    const subtotalRooms = (form.custom_total !== "" && Number(form.custom_total) > 0)
      ? Number(form.custom_total)
      : roomsTotal;
    // Sommo gli extra (letto extra, animale, parcheggio, navetta, tassa di
    // soggiorno con moltiplicatore ospiti, voce libera …).
    // Formula uniforme: qty × guests × unit_price (guests default 1).
    const extras = (form.items || []).reduce((s, it) => {
      const g = Math.max(1, Number(it.guests || 1));
      return s + Number(it.qty || 0) * g * Number(it.unit_price || 0);
    }, 0);
    // Lo sconto (% o €) si applica al SALDO TOTALE (camere + extra) —
    // regola concordata con l'host: uno sconto commerciale del 10%
    // riduce il prezzo finale del 10% inclusa la tassa di soggiorno.
    const subtotalFull = +(subtotalRooms + extras).toFixed(2);
    const discount = computeDiscountAmount(subtotalFull);
    return +Math.max(0, subtotalFull - discount).toFixed(2);
  }, [form.custom_total, form.items, form.discount_pct, form.discount_type, roomsTotal]);

  // Load lightweight rooms catalog so the "+ Aggiungi camera" picker shows
  // the real room types from the property (Matrimoniale, Tripla, …) with
  // the published base rate. This is NOT linked to availability — the quote
  // is purely commercial.
  const loadRoomsCatalog = useCallback(async () => {
    try {
      const r = await api.get("/rooms");
      setRoomsCatalog(Array.isArray(r.data) ? r.data : []);
    } catch { setRoomsCatalog([]); }
  }, []);
  useEffect(() => { loadRoomsCatalog(); }, [loadRoomsCatalog]);

  // Load configured rate plans from the admin (Standard, Flessibile -21gg,
  // Non rimborsabile -10%, Tariffa mensile -20%, …). Filtering by active
  // happens client-side so we don't lose plans temporarily disabled.
  const loadRatePlans = useCallback(async () => {
    try {
      const r = await api.get("/admin/rate-plans");
      const plans = Array.isArray(r.data?.rate_plans) ? r.data.rate_plans : [];
      const active = plans.filter((p) => p.active !== false);
      setRatePlans(active);
      // Pre-seleziona TUTTI i piani attivi (il default più utile per l'host)
      setSelectedRatePlans(active.map((p) => p.id));
    } catch { setRatePlans([]); }
  }, []);
  useEffect(() => { loadRatePlans(); }, [loadRatePlans]);

  const toggleRatePlan = (id) => {
    setSelectedRatePlans((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Voci extra preset gestite da admin (loaded da /admin/quote-extras).
  // L'host aggiunge nuovi extra dalla pagina di gestione e li vede subito qui
  // come dropdown selezionabile. Se la collezione è vuota, il backend la
  // seed automaticamente con 6 voci comuni (letto, animale, parcheggio, ecc.).
  const [quoteExtras, setQuoteExtras] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/admin/quote-extras")
      .then((r) => setQuoteExtras(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setQuoteExtras([]));
  }, [open]);

  // ===== Voci extra =====
  // Regole di calcolo automatico della quantità (l'host può sempre
  // sovrascrivere il campo qty a mano dopo l'aggiunta):
  //  - per_night           → MAX notti fra tutte le camere del preventivo
  //  - per_guest           → SOMMA ospiti su TUTTE le camere del preventivo
  //    (iter221 fix: prima usava solo la camera principale → in preventivi
  //    multi-camera contava solo il primo gruppo. Es. 6 + 5 ospiti in due
  //    camere → tassa di soggiorno fatturata per 6 invece di 11).
  //  - per_guest_per_night → SOMMA(ospiti × notti) su TUTTE le camere.
  //    Esempio: 2 camere (2 ospiti × 3 notti + 3 ospiti × 3 notti = 15).
  //    Corretto per la tassa di soggiorno (€1/persona/notte) su preventivi
  //    multi-camera con occupazioni diverse.
  //  - flat                → 1
  const computePresetQty = (mode) => {
    const rooms = form.rooms || [];
    if (mode === "per_night") {
      const maxNights = rooms.reduce(
        (m, r) => Math.max(m, Number(r.nights || 1)),
        Number(primaryRoom.nights || 1),
      );
      return Math.max(1, maxNights);
    }
    if (mode === "per_guest") {
      const totalGuests = rooms.reduce(
        (s, r) => s + Math.max(1, Number(r.guests || 2)),
        0,
      );
      return Math.max(1, totalGuests || Number(primaryRoom.guests || 2));
    }
    if (mode === "per_guest_per_night") {
      const totalNightPersons = rooms.reduce((sum, r) => {
        const n = Math.max(1, Number(r.nights || 1));
        const g = Math.max(1, Number(r.guests || 2));
        return sum + n * g;
      }, 0);
      return Math.max(1, totalNightPersons);
    }
    return 1; // flat
  };
  const addPresetItem = (extra) => {
    // Il campo `guests` è ora un moltiplicatore universale (default 1) che
    // l'host può aumentare per qualsiasi voce — es. "letto aggiunto" per 2
    // persone. Per la tassa di soggiorno lo pre-popoliamo con il totale
    // ospiti così l'host non deve rifare il conto.
    // iter233 fix: per `per_guest_per_night` (tassa di soggiorno) NON si
    // sommano le notti tra le camere — tutte le camere di uno stesso
    // soggiorno condividono di norma le stesse date. Prima il codice
    // faceva `totalNights = SOMMA(nights)` → per 2 camere × 2 ospiti × 1
    // notte generava qty=2 e guests=4 → 2×4×1=€8, mentre il corretto è
    // €4 (4 persone-notte). Ora: se tutte le camere hanno le stesse
    // notti usiamo `qty = nights, guests = totalGuests` (leggibile);
    // se differiscono usiamo `qty = sum(n×g), guests = 1` (matematicamente
    // sempre corretto anche con notti disomogenee).
    const isPerGuestNight = extra.qty_mode === "per_guest_per_night";
    const rooms = form.rooms || [];
    const nightsList = rooms.length > 0
      ? rooms.map((r) => Math.max(1, Number(r.nights || 1)))
      : [Math.max(1, Number(primaryRoom.nights || 1))];
    const allSameNights = nightsList.every((n) => n === nightsList[0]);
    const totalGuests = rooms.length > 0
      ? rooms.reduce((s, r) => s + Math.max(1, Number(r.guests || 2)), 0)
      : Math.max(1, Number(primaryRoom.guests || 2));
    const totalNightPersons = rooms.length > 0
      ? rooms.reduce((sum, r) => {
          const n = Math.max(1, Number(r.nights || 1));
          const g = Math.max(1, Number(r.guests || 2));
          return sum + n * g;
        }, 0)
      : Math.max(1, Number(primaryRoom.nights || 1)) * Math.max(1, Number(primaryRoom.guests || 2));
    let presetQty;
    let presetGuests;
    if (isPerGuestNight) {
      if (allSameNights) {
        presetQty = nightsList[0];
        presetGuests = totalGuests;
      } else {
        presetQty = totalNightPersons;
        presetGuests = 1;
      }
    } else {
      presetQty = computePresetQty(extra.qty_mode);
      presetGuests = 1;
    }
    setForm((f) => ({
      ...f,
      items: [...(f.items || []), {
        description: extra.name,
        qty: presetQty,
        guests: presetGuests,
        qty_mode: extra.qty_mode || "flat",
        unit_price: Number(extra.unit_price || 0),
        _uid: `${extra.id || extra.name}-${Date.now()}`,
      }],
    }));
  };

  // iter233b — Auto-ricalcola gli item con `qty_mode` (tassa di soggiorno,
  // extra per notte, extra per ospite) quando l'host modifica le camere
  // del preventivo (aggiunge / rimuove camere, cambia notti o ospiti).
  // Altrimenti l'host che aggiunge la tassa quando c'è 1 sola camera e
  // POI aggiunge la seconda ritrova la tassa vecchia (€2 invece di €4)
  // → identica trappola del calcolo sbagliato che stiamo fixando.
  // NB: se l'host modifica manualmente qty/guests, li rispettiamo — quindi
  // ricalcoliamo solo se il valore attuale corrisponde a ciò che avremmo
  // generato in automatico prima. In pratica: se l'item non ha `_manual`
  // flag su qty/guests, resincronizziamo.
  useEffect(() => {
    if (!Array.isArray(form.items) || form.items.length === 0) return;
    const rooms = form.rooms || [];
    if (rooms.length === 0) return;
    const nightsList = rooms.map((r) => Math.max(1, Number(r.nights || 1)));
    const allSameNights = nightsList.every((n) => n === nightsList[0]);
    const totalGuests = rooms.reduce((s, r) => s + Math.max(1, Number(r.guests || 2)), 0);
    const totalNightPersons = rooms.reduce((sum, r) => {
      const n = Math.max(1, Number(r.nights || 1));
      const g = Math.max(1, Number(r.guests || 2));
      return sum + n * g;
    }, 0);
    const maxNights = nightsList.reduce((m, n) => Math.max(m, n), 1);
    let changed = false;
    const newItems = form.items.map((it) => {
      if (it._manual) return it;
      const mode = it.qty_mode;
      if (!mode || mode === "flat") return it;
      let newQty = it.qty;
      let newGuests = it.guests || 1;
      if (mode === "per_guest_per_night") {
        if (allSameNights) {
          newQty = nightsList[0];
          newGuests = totalGuests;
        } else {
          newQty = totalNightPersons;
          newGuests = 1;
        }
      } else if (mode === "per_night") {
        newQty = maxNights;
        newGuests = 1;
      } else if (mode === "per_guest") {
        newQty = totalGuests;
        newGuests = 1;
      }
      if (newQty !== it.qty || newGuests !== (it.guests || 1)) {
        changed = true;
        return { ...it, qty: newQty, guests: newGuests };
      }
      return it;
    });
    if (changed) {
      setForm((f) => ({ ...f, items: newItems }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.rooms]);
  const addFreeItem = () => {
    setForm((f) => ({
      ...f,
      items: [...(f.items || []), { description: "", qty: 1, unit_price: 0, _uid: `free-${Date.now()}` }],
    }));
  };
  const updateItem = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => {
        if (i !== idx) return it;
        // iter233b — se l'host modifica manualmente qty o guests, segna
        // l'item come `_manual` così l'auto-ricalcolo su cambio camere
        // non sovrascrive il suo valore.
        const isManualEdit = field === "qty" || field === "guests";
        return { ...it, [field]: value, ...(isManualEdit ? { _manual: true } : {}) };
      }),
    }));
  };
  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const loadArchive = useCallback(async () => {
    try {
      const r = await api.get("/admin/quotes/archive");
      setArchive(r.data?.items || []);
    } catch { setArchive([]); }
  }, []);
  useEffect(() => { loadArchive(); }, [loadArchive]);

  const generate = async () => {
    if (!form.client_name.trim()) return toast.error("Inserisci il nome del cliente");
    // Almeno una camera selezionata OPPURE una voce extra
    const hasValidRoom = (form.rooms || []).some((r) => r.room_id);
    if (!hasValidRoom && form.items.length === 0) {
      return toast.error("Seleziona almeno una camera o aggiungi una voce extra");
    }
    setBuilding(true);
    try {
      // Costruisce la lista dei piani selezionati per il PDF di confronto.
      const planObjects = ratePlans
        .filter((p) => selectedRatePlans.includes(p.id))
        .map((p) => ({
          name: p.name,
          cancellation_type: p.cancellation_type || "flexible_7d",
          discount_pct: Number(p.discount_pct || 0),
        }));
      const payload = {
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        arrival: form.arrival,
        departure: form.departure || "",
        // Notti = MAX delle camere (NON somma): le camere sono in parallelo,
        // non in serie. Es. 4 camere × 4 notti = soggiorno di 4 notti.
        nights: Math.max(1, ...(form.rooms || []).map((r) => Math.max(1, Number(r.nights || 1)))),
        notes: form.notes,
        rooms: (form.rooms || []).map((r) => ({
          room_id: r.room_id || "",
          nights: Math.max(1, Number(r.nights || 1)),
          guests: Number(r.guests || 2),
          ...(r.custom_nightly_rate !== "" && Number(r.custom_nightly_rate) > 0
            ? { custom_nightly_rate: Number(r.custom_nightly_rate) }
            : {}),
        })),
        room_id: primaryRoom.room_id || "",
        guests: Number(primaryRoom.guests || 2),
        rate_plans: planObjects,
        // Voci extra inserite dall'host (letto, animale, parcheggio, tassa
        // di soggiorno, ecc.): verranno aggiunte sotto le righe "Pernottamento"
        // nel PDF. La quantità effettiva è `qty × guests` (guests default 1);
        // moltiplico prima dell'invio così il backend, che calcola
        // qty × unit_price, ottiene il totale corretto. Se guests > 1
        // aggiungo il breakdown nella descrizione per trasparenza al cliente.
        items: (form.items || []).map(({ _uid, _manual, guests, qty_mode, ...it }) => {
          const qty = Math.max(0, Number(it.qty || 0));
          const gg = Math.max(1, Number(guests || 1));
          const unit = Number(it.unit_price || 0);
          let desc = it.description || "—";
          if (gg > 1 && qty > 0) {
            const unitStr = unit.toFixed(2);
            desc = `${desc} (${qty} × ${gg} × €${unitStr})`;
          }
          return { description: desc, qty: qty * gg, unit_price: unit };
        }),
        ...(form.custom_total !== "" ? { custom_total: Number(form.custom_total) } : {}),
        // Sconto: se in %, invio come `plan_discount_pct` negativo; se in €,
        // invio come `discount_euro` positivo. Il backend applica lo sconto
        // al subtotale camere (o al forfait) prima degli extra.
        ...(form.discount_pct !== "" && Number(form.discount_pct) > 0
          ? (form.discount_type === "euro"
            ? { discount_euro: Math.max(0, Number(form.discount_pct)) }
            : { plan_discount_pct: -Math.max(0, Math.min(100, Number(form.discount_pct))) })
          : {}),
        ...(editingQuoteId ? { replace_id: editingQuoteId } : {}),
      };
      const r = await api.post("/admin/quotes/generate", payload, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const safe = form.client_name.replace(/[^a-zA-Z0-9]+/g, "_");
      const fn = m ? m[1] : `preventivo_${safe}_${todayISO()}.pdf`;
      saveBlob(r.data, fn);
      toast.success(editingQuoteId ? "Preventivo aggiornato (sostituisce il precedente)" : "Preventivo generato");
      // Reset editing state per evitare di sovrascrivere altri preventivi per errore
      setEditingQuoteId(null);
      await loadArchive();
    } catch (e) {
      toast.error(errMsg(e, "Errore generazione"));
    } finally { setBuilding(false); }
  };

  const downloadArchived = async (q) => {
    try {
      const r = await api.get(`/admin/quotes/archive/${q.id}`, { responseType: "blob" });
      saveBlob(r.data, q.filename);
      toast.success(`${q.filename} scaricato`);
    } catch { toast.error("Errore"); }
  };

  // Anteprima PDF in modale inline (iframe). Usa endpoint HTTPS reale con
  // ?token=<JWT>&inline=1 invece di blob URL — i blob URL venivano bloccati
  // da adblocker (ERR_BLOCKED_BY_CLIENT) e dalla CSP del browser dentro iframe.
  const previewArchived = (q) => {
    const t = getToken();
    if (!t) { toast.error("Sessione scaduta. Effettua nuovamente il login."); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/admin/quotes/archive/${q.id}?inline=1&token=${encodeURIComponent(t)}`;
    setPdfPreview({ url, filename: q.filename });
  };

  const closePdfPreview = () => {
    setPdfPreview(null);
  };

  const deleteArchived = async (q) => {
    if (!window.confirm(`Eliminare "${q.filename}"?`)) return;
    try {
      await api.delete(`/admin/quotes/archive/${q.id}`);
      await loadArchive();
      toast.success("Eliminato");
    } catch { toast.error("Errore"); }
  };

  // iter220 — WhatsApp con link pubblico al PDF preventivo + menzione email.
  // Mirror di sendWhatsAppWithPdf() delle ricevute: crea (o riusa) uno
  // share_token, apre wa.me con testo che (a) informa che il PDF è stato
  // spedito anche via email, (b) allega il link diretto al PDF.
  const sendWhatsAppQuoteWithPdf = async (q) => {
    if (!q.client_phone) {
      toast.error("Numero di telefono mancante sul preventivo");
      return;
    }
    try {
      const r = await api.post(`/admin/quotes/archive/${q.id}/share-link`);
      const pdfUrl = r.data?.url;
      if (!pdfUrl) throw new Error("Link PDF non disponibile");
      const firstName = (q.client_name || "").trim().split(/\s+/)[0] || "";
      const greeting = firstName ? `Ciao ${firstName},` : "Ciao,";
      const hasEmail = !!(q.client_email && q.client_email.includes("@"));
      const msg = hasEmail
        ? (
            `${greeting} ti ho appena inviato via email il preventivo per il B&B Emmanuel `
            + `(controlla anche lo spam).\n\n`
            + `📄 In alternativa lo trovi qui: ${pdfUrl}\n\n`
            + `A presto!`
          )
        : (
            `${greeting} ti mando il preventivo per il tuo soggiorno al B&B Emmanuel `
            + `a Reggio Calabria.\n\n`
            + `📄 Scarica il PDF: ${pdfUrl}\n\n`
            + `A presto!`
          );
      const url = buildWhatsAppLink(q.client_phone, msg);
      if (!url) {
        toast.error("Numero non valido");
        return;
      }
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up bloccato. Abilitalo per aprire WhatsApp.");
    } catch (e) {
      toast.error(errMsg(e, "Errore preparazione link WhatsApp"));
    }
  };

  return (
    <div className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="quotes-section">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          className="flex items-center gap-2 text-left flex-1 hover:opacity-80"
          data-testid="quotes-panel-collapse"
          title={panelOpen ? "Nascondi Preventivi" : "Mostra Preventivi"}
        >
          <ChevronDown className={`w-5 h-5 text-[#635F59] transition-transform ${panelOpen ? "" : "-rotate-90"}`} />
          <h4 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#AC5D45]" /> Preventivi PDF
          </h4>
        </button>
        {panelOpen && (open ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { if (editingQuoteId) { resetForm(); } setOpen(false); }}
            className="text-xs border-[#635F59] text-[#635F59] hover:bg-[#F5F0E8]"
            data-testid="quotes-toggle"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Chiudi form
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={() => { setOpen(true); setTimeout(() => window.scrollTo({ top: document.querySelector('[data-testid="quotes-section"]').offsetTop - 20, behavior: 'smooth' }), 50); }}
            className="text-xs bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            data-testid="quotes-toggle"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo preventivo
          </Button>
        ))}
      </div>
      {panelOpen && (
      <>

      {open && (
        <div className="mt-4 space-y-4">
          {editingQuoteId && (
            <div className="bg-[#FFE5D9] border-2 border-[#AC5D45] rounded-lg p-3 flex items-center justify-between gap-3" data-testid="q-editing-banner">
              <div className="flex items-center gap-2 text-sm text-[#7A2E1A]">
                <Edit3 className="w-4 h-4" />
                <span><b>Modifica preventivo in corso.</b> Cliccando "Genera preventivo PDF" verrà <b>sostituito</b> il precedente.</span>
              </div>
              <Button onClick={resetForm} size="sm" variant="outline" className="text-[11px] border-[#AC5D45] text-[#7A2E1A] hover:bg-[#FFD0B8]" data-testid="q-cancel-edit">
                Annulla modifica
              </Button>
            </div>
          )}
          <p className="text-sm text-[#635F59]">
            Crea un preventivo personalizzato in PDF (cliente, periodo, righe descrittive, totale, note).
            Pronto da scaricare e inviare via email/WhatsApp.
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome cliente *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Es. Mario Rossi" data-testid="q-name" />
            </div>
            <div>
              <Label className="text-xs">Telefono / WhatsApp (consigliato)</Label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="+39 320 123 4567" data-testid="q-phone" />
            </div>
            <div>
              <Label className="text-xs">Email (opzionale)</Label>
              <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="mario@email.it" data-testid="q-email" />
            </div>
          </div>

          {/* Camere & Soggiorno — supporta N camere (multi-room).
              Ogni camera ha la sua tipologia, notti, ospiti e tariffa custom.
              Genera N righe "Pernottamento" separate nel PDF. */}
          <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 space-y-3" data-testid="q-room-block">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-xs font-semibold text-[#1E3A5F]">
                Camere & Soggiorno
                {(form.rooms || []).length > 1 && (
                  <span className="ml-2 text-[10px] bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded-full">{form.rooms.length} camere</span>
                )}
              </Label>
              <Button type="button" onClick={addRoom} size="sm" variant="outline" className="text-[11px] h-7 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5" data-testid="q-add-room">
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi camera
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-[#635F59]">Data arrivo</Label>
                <DateInputIT value={form.arrival} onChange={(e) => setArrivalDeparture(e.target.value, form.departure)} data-testid="q-arrival" />
              </div>
              <div>
                <Label className="text-[10px] text-[#635F59]">Data partenza <span className="text-[9px] text-[#635F59]/70">(calcola le notti)</span></Label>
                <DateInputIT value={form.departure} onChange={(e) => setArrivalDeparture(form.arrival, e.target.value)} data-testid="q-departure" />
              </div>
            </div>
            {form.arrival && form.departure && computeNightsFromDates(form.arrival, form.departure) && (
              <div className="text-[11px] text-[#1E3A5F] bg-[#FDFBF7] border border-[#E8E2D9] rounded px-2 py-1" data-testid="q-auto-nights">
                Soggiorno calcolato: <b>{computeNightsFromDates(form.arrival, form.departure)} notti</b> · le notti delle camere vuote vengono compilate automaticamente
              </div>
            )}
            {(form.rooms || []).map((r, idx) => {
              const rDoc = findRoomById(r.room_id);
              const rBaseRate = Number(rDoc?.price_per_night || 0);
              const rNights = Math.max(1, Number(r.nights || 1));
              // Tariffa stagionale (override calendar) per questa riga, se l'host
              // ha impostato l'arrivo. Cade su rBaseRate se non disponibile.
              const rSeasonalKey = `${r.room_id}|${form.arrival}|${rNights}`;
              const rSeasonalRate = form.arrival && seasonalRates[rSeasonalKey] > 0
                ? seasonalRates[rSeasonalKey]
                : 0;
              // Priorità: custom_nightly_rate (host override) > seasonal > base.
              const rCustom = r.custom_nightly_rate !== "" && Number(r.custom_nightly_rate) > 0
                ? Number(r.custom_nightly_rate)
                : (rSeasonalRate > 0 ? rSeasonalRate : rBaseRate);
              // Placeholder mostra la tariffa stagionale se diversa dalla base.
              const rRate = rSeasonalRate > 0 ? rSeasonalRate : rBaseRate;
              const rIsSeasonal = rSeasonalRate > 0 && Math.abs(rSeasonalRate - rBaseRate) > 0.005;
              const rSubtotal = rCustom * rNights;
              return (
                <div key={r._uid} className="bg-white border border-[#E8E2D9] rounded-lg p-3 relative" data-testid={`q-room-row-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-[#7A5410]">Camera {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => duplicateRoom(r._uid)} className="text-[10px] px-2 py-0.5 rounded bg-[#FAF7F2] hover:bg-[#F0E6D6] text-[#7A5410] border border-[#E8E2D9]" title="Duplica camera" data-testid={`q-room-dup-${idx}`}>
                        Duplica
                      </button>
                      {(form.rooms || []).length > 1 && (
                        <button type="button" onClick={() => removeRoom(r._uid)} className="text-rose-600 hover:bg-rose-50 rounded p-1" title="Rimuovi camera" data-testid={`q-room-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-[10px] text-[#635F59]">Tipologia *</Label>
                      <select
                        value={r.room_id}
                        onChange={(e) => updateRoom(r._uid, { room_id: e.target.value })}
                        className="h-9 w-full text-sm rounded border border-[#E8E2D9] bg-white px-2"
                        data-testid={`q-room-select-${idx}`}
                      >
                        <option value="">— Seleziona tipologia camera —</option>
                        {roomsCatalog.map((rm) => {
                          const rn = typeof rm.name === "string" ? rm.name : (rm.name?.it || rm.name?.en || "Camera");
                          return (
                            <option key={rm.id} value={rm.id}>
                              {rn} — € {Number(rm.price_per_night || 0).toFixed(2)}/notte
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                      <div>
                        <Label className="text-[10px] text-[#635F59]">Notti</Label>
                        <Input
                          type="number" min="1" max="60"
                          placeholder="es. 3"
                          value={r.nights}
                          onChange={(e) => updateRoom(r._uid, { nights: e.target.value })}
                          onFocus={(e) => e.target.select()}
                          data-testid={`q-nights-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#635F59]">N° ospiti</Label>
                        <Input
                          type="number" min="1" max="20"
                          placeholder="es. 2"
                          value={r.guests}
                          onChange={(e) => updateRoom(r._uid, { guests: e.target.value })}
                          onFocus={(e) => e.target.select()}
                          data-testid={`q-guests-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#635F59]">Tariffa/notte (€)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          placeholder={rRate > 0 ? `default ${rRate.toFixed(2)}` : "—"}
                          value={r.custom_nightly_rate}
                          onChange={(e) => updateRoom(r._uid, { custom_nightly_rate: e.target.value })}
                          onFocus={(e) => e.target.select()}
                          data-testid={`q-custom-nightly-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                  {r.room_id && (
                    <div className="mt-2 text-[11px] text-[#635F59] text-right">
                      Subtotale camera: <b className="text-[#AC5D45]">€ {rSubtotal.toFixed(2)}</b>
                      <span className="text-[#635F59]/70"> ({rNights} notti × € {rCustom.toFixed(2)})</span>
                      {rIsSeasonal && (
                        <span
                          className="ml-2 inline-block px-1.5 py-0.5 rounded bg-[#E3A857]/20 text-[#7A5410] text-[10px] font-semibold"
                          title={`Tariffa stagionale dal calendario · base € ${rBaseRate.toFixed(2)}/notte`}
                          data-testid={`q-seasonal-badge-${idx}`}
                        >
                          ★ Tariffa stagionale
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {roomsTotal > 0 && (form.rooms || []).length > 1 && (
              <div className="text-right text-xs font-semibold text-[#1E3A5F] border-t border-[#E8E2D9] pt-2" data-testid="q-rooms-total">
                Totale pernottamento (tutte le camere): <span className="text-[#AC5D45]">€ {roomsTotal.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Piani tariffari da includere nel PDF (multi-selezione).
              Ogni piano selezionato compare nel PDF come riga di confronto con:
                Nome · Cancellazione · Sconto · Totale · Acconto · Saldo
              L'acconto è calcolato automaticamente in base al cancellation_type
              di ciascun piano:
                · flexible_*    → 30% acconto + 70% al check-in
                · non_refundable / monthly → 100% pagamento anticipato */}
          {ratePlans.length > 0 && (
            <div className="bg-[#FFF4D6] border border-[#E3A857] rounded-lg p-3" data-testid="q-rate-plans-block">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-[#7A5410] flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 text-[#E3A857]" /> Piani tariffari da includere nel PDF
                  <span className="text-[10px] text-[#7A5410]/80 font-normal">
                    ({selectedRatePlans.length} / {ratePlans.length})
                  </span>
                </Label>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <button
                    type="button"
                    onClick={() => setSelectedRatePlans(ratePlans.map((p) => p.id))}
                    className="text-[#7A5410] hover:underline"
                    data-testid="q-rp-select-all"
                  >
                    Tutti
                  </button>
                  <span className="text-[#7A5410]/40">·</span>
                  <button
                    type="button"
                    onClick={() => setSelectedRatePlans([])}
                    className="text-[#7A5410] hover:underline"
                    data-testid="q-rp-deselect-all"
                  >
                    Nessuno
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {ratePlans.map((p) => {
                  const checked = selectedRatePlans.includes(p.id);
                  const discRaw = Number(p.discount_pct || 0);
                  const discAbs = Math.abs(discRaw);
                  const isPrepay = p.cancellation_type === "non_refundable" || p.cancellation_type === "monthly";
                  const estTotal = discRaw ? total * (1 + discRaw / 100) : total;
                  const estDeposit = isPrepay ? estTotal : +(estTotal * 0.30).toFixed(2);
                  const estBalance = +(estTotal - estDeposit).toFixed(2);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition ${checked ? "bg-white border-[#E3A857] shadow-sm" : "bg-white/40 border-[#E3A857]/30 hover:bg-white/70"}`}
                      data-testid={`q-rp-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRatePlan(p.id)}
                        className="mt-0.5 w-3.5 h-3.5 accent-[#AC5D45]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#1E3A5F] flex items-center gap-1.5 flex-wrap">
                          <span className="truncate">{p.name}</span>
                          {discRaw < 0 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-[#E3A857] text-[#1E3A5F] font-bold">−{discAbs}%</span>
                          )}
                          {discRaw > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-rose-200 text-rose-800 font-bold">+{discRaw}%</span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#635F59] mt-0.5">
                          {isPrepay ? "Pagamento totale subito" : "Acconto 30% · saldo al check-in"}
                          {total > 0 && (
                            <span className="block">
                              Totale <b className="text-[#AC5D45]">€ {estTotal.toFixed(2)}</b> · Acconto <b>€ {estDeposit.toFixed(2)}</b> · Saldo <b>€ {estBalance.toFixed(2)}</b>
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#7A5410]/80 mt-2 italic">
                L'acconto del 30% si applica automaticamente alle tariffe rimborsabili. Le tariffe non rimborsabili richiedono il pagamento totale alla conferma.
              </p>
            </div>
          )}

          {/* Totale forfait + Sconto (opzionali) — il forfait sovrascrive
              l'intero pernottamento; lo sconto si applica al subtotale
              camere prima degli extra. Le tariffe per-camera sono gestite
              direttamente nel blocco "Camere & Soggiorno". */}
          <div className="bg-[#FAF7F2] border border-[#E8E2D9] rounded-lg p-3" data-testid="q-custom-price-block">
            <Label className="text-xs font-semibold text-[#1E3A5F] mb-2 block">Forfait & Sconto (opzionali)</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-[#635F59]">Totale fisso pernottamento (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="es. 200.00 — vuoto = usa tariffe sopra"
                  value={form.custom_total}
                  onChange={(e) => setForm({ ...form, custom_total: e.target.value })}
                  onFocus={(e) => e.target.select()}
                  data-testid="q-custom-total"
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#635F59]">
                  Sconto ({form.discount_type === "euro" ? "€" : "%"})
                </Label>
                <div className="flex gap-1.5">
                  <Input
                    type="number"
                    step={form.discount_type === "euro" ? "0.01" : "1"}
                    min="0"
                    {...(form.discount_type === "percent" ? { max: 100 } : {})}
                    placeholder={form.discount_type === "euro" ? "es. 20.00" : "es. 10"}
                    value={form.discount_pct}
                    onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    data-testid="q-discount-pct"
                    className="flex-1"
                  />
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="h-9 px-2 rounded-md border border-input bg-white text-sm font-semibold text-[#7A5410] cursor-pointer hover:bg-[#FFF4D6]"
                    data-testid="q-discount-type"
                    title="Tipo sconto: percentuale o valore assoluto"
                  >
                    <option value="percent">%</option>
                    <option value="euro">€</option>
                  </select>
                </div>
              </div>
              <div className="text-[11px] text-[#635F59] flex items-center">
                Lo sconto (in % o in €) si applica al <b>saldo totale</b> del preventivo (camere + extra). Funziona anche insieme al forfait.
              </div>
            </div>
          </div>

          {/* Voci extra (oltre al pernottamento auto-calcolato).
              Bottoni rapidi per letti aggiunti / animali / parcheggi / navetta
              / colazione / late check-out + "Voce libera" per casi atipici.
              Sopra le voci appaiono i titoli "Descrizione · Qtà · Tariffa
              unit." che ricompaiono identici nel PDF cliente. */}
          <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid="q-items-block">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Label className="text-xs font-semibold text-[#1E3A5F]">Voci aggiuntive (extra a pagamento)</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {quoteExtras.length > 0 && (
                  <select
                    onChange={(e) => {
                      const ex = quoteExtras.find((x) => x.id === e.target.value);
                      if (ex) addPresetItem(ex);
                      e.target.value = "";
                    }}
                    className="h-8 text-xs rounded border border-[#E3A857] bg-white px-2 text-[#7A5410] font-semibold cursor-pointer hover:bg-[#FFF4D6]"
                    data-testid="q-extras-dropdown"
                    defaultValue=""
                  >
                    <option value="">+ Servizio extra ▼</option>
                    {quoteExtras.filter((x) => x.active !== false).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name} — € {Number(x.unit_price || 0).toFixed(2)}
                        {x.qty_mode === "per_night"
                          ? " / notte"
                          : x.qty_mode === "per_guest"
                          ? " / ospite"
                          : x.qty_mode === "per_guest_per_night"
                          ? " / ospite / notte"
                          : ""}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={addFreeItem}
                  className="text-[11px] px-3 py-1.5 rounded-md bg-[#AC5D45] text-white hover:bg-[#8A4936] font-semibold whitespace-nowrap"
                  data-testid="q-add-free-item"
                >
                  + Voce libera
                </button>
              </div>
            </div>
            {form.items.length > 0 && (
              <>
                {/* Column headers — these appear identically in the PDF */}
                <div className="grid grid-cols-12 gap-2 px-1 mb-1 text-[10px] uppercase tracking-wider text-[#635F59] font-semibold">
                  <div className="col-span-5">Descrizione</div>
                  <div className="col-span-2 text-center">Notti</div>
                  <div className="col-span-1 text-center">Pers.</div>
                  <div className="col-span-2 text-right">Tariffa unit.</div>
                  <div className="col-span-2 text-right pr-7">Totale</div>
                </div>
                <div className="space-y-1.5">
                  {form.items.map((it, idx) => {
                    // Il totale della riga è sempre `qty × guests × unit_price`.
                    // Per default `guests` = 1 (extra "flat"/per notte come letto,
                    // parcheggio ecc.) oppure = totale ospiti del preventivo per
                    // la tassa di soggiorno (`per_guest_per_night`). L'host può
                    // sempre modificare la casella (es. letto aggiunto per 2
                    // persone → mette 2 e la tariffa raddoppia).
                    const effGuests = Math.max(1, Number(it.guests || 1));
                    const lineTot = Number(it.qty || 0) * effGuests * Number(it.unit_price || 0);
                    return (
                      <div key={it._uid || idx} className="grid grid-cols-12 gap-2 items-end sm:items-center bg-white border border-[#E8E2D9] rounded p-2" data-testid={`q-item-row-${idx}`}>
                        <div className="col-span-12 sm:col-span-5">
                          <Label className="text-[10px] text-[#635F59] sm:hidden block mb-0.5">Descrizione</Label>
                          <Input
                            placeholder="Descrizione voce"
                            value={it.description}
                            onChange={(e) => updateItem(idx, "description", e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`q-item-desc-${idx}`}
                          />
                        </div>
                        <div className="col-span-3 sm:col-span-2">
                          <Label className="text-[10px] text-[#635F59] sm:hidden block mb-0.5" title="Numero di notti o quantità totale">Notti</Label>
                          <Input
                            type="number" min="1" step="1"
                            value={it.qty}
                            onChange={(e) => updateItem(idx, "qty", parseInt(e.target.value, 10) || 0)}
                            className="h-8 text-xs text-center"
                            data-testid={`q-item-qty-${idx}`}
                            title="Numero notti o quantità"
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <Label className="text-[10px] text-[#635F59] sm:hidden block mb-0.5" title="Numero di persone (moltiplicatore)">Pers.</Label>
                          <Input
                            type="number" min="1" step="1"
                            value={it.guests || 1}
                            onChange={(e) => updateItem(idx, "guests", parseInt(e.target.value, 10) || 1)}
                            className="h-8 text-xs text-center"
                            data-testid={`q-item-guests-${idx}`}
                            title="Numero persone (moltiplicatore)"
                          />
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="text-[10px] text-[#635F59] sm:hidden block mb-0.5">Prezzo €</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={it.unit_price}
                            onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                            className="h-8 text-xs text-right"
                            data-testid={`q-item-price-${idx}`}
                          />
                        </div>
                        <div className="col-span-3 sm:col-span-2 flex items-center justify-end gap-1">
                          <span className="text-xs font-semibold text-[#AC5D45] min-w-[60px] text-right">€ {lineTot.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="w-7 h-7 sm:w-6 sm:h-6 inline-flex items-center justify-center rounded hover:bg-rose-50 shrink-0"
                            title="Rimuovi"
                            data-testid={`q-item-remove-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {form.items.length === 0 && (
              <p className="text-[11px] text-[#635F59] italic">Nessuna voce extra. La camera selezionata sopra è già inclusa nel preventivo.</p>
            )}
          </div>

          <div>
            <Label className="text-xs">Note / condizioni (opzionali)</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Es. Validità preventivo 7 giorni. Colazione inclusa."
              data-testid="q-notes"
            />
          </div>

          {/* Riepilogo: totale finale del preventivo (Standard Rate).
              I totali per-piano compaiono nel PDF nella tabella di
              confronto, con acconto/saldo calcolati per ognuno secondo
              il cancellation_type. */}
          <div className="bg-gradient-to-r from-[#FFF4D6] to-[#FDFBF7] border border-[#E3A857] rounded-lg p-4" data-testid="q-summary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#635F59]">
                Totale preventivo (Standard Rate)
                {(form.discount_pct !== "" && Number(form.discount_pct) > 0) && (
                  <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-[9px] bg-[#AC5D45] text-white font-bold">
                    sconto applicato
                  </span>
                )}
              </span>
              <span className="font-bold text-[#1E3A5F] text-lg" data-testid="q-summary-total">€ {total.toFixed(2)}</span>
            </div>
            <p className="text-[10px] text-[#635F59] mt-2 italic">
              Include tutte le voci del preventivo (camere + extras) meno lo sconto se impostato. Nel PDF il cliente vede la tabella di confronto con tutti i piani selezionati. L'acconto viene calcolato automaticamente: 30% per le tariffe rimborsabili, 100% per quelle non rimborsabili.
            </p>
          </div>

          <Button onClick={generate} disabled={building || !form.client_name || !primaryRoom.room_id} className={`w-full text-white ${editingQuoteId ? "bg-[#AC5D45] hover:bg-[#8A4936]" : "bg-[#1E3A5F] hover:bg-[#152A45]"}`} data-testid="q-generate">
            {building ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Genero…</> : editingQuoteId ? <><Edit3 className="w-4 h-4 mr-2" /> Aggiorna preventivo (sostituisce)</> : <><Download className="w-4 h-4 mr-2" /> Genera preventivo PDF</>}
          </Button>
        </div>
      )}

      {/* Archive — sempre visibile, indipendente dal form */}
      <div className={open ? "mt-4" : "mt-4 pt-4 border-t border-[#E8E2D9]"}>
          {/* Archive */}
          <div className="pt-3 border-t border-[#E8E2D9]">
            <div className="text-xs uppercase tracking-wider text-[#635F59] mb-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Folder className="w-3.5 h-3.5" /> Archivio preventivi · {(() => {
                  const s = archiveSearch.trim().toLowerCase();
                  const matches = (q) => {
                    if (s && !((q.quote_number || "").toLowerCase().includes(s) ||
                      (q.client_name || "").toLowerCase().includes(s) ||
                      (q.filename || "").toLowerCase().includes(s) ||
                      (q.client_phone || "").toLowerCase().includes(s))) return false;
                    if (archiveOnlyConfirmed && !(q.status === "confirmed" && q.converted_booking_code)) return false;
                    if (archiveAge !== "all") {
                      const a = quoteAgeInfo(q.generated_at);
                      if (!a) return false;
                      if (archiveAge === "stale" && a.tone !== "stale") return false;
                      if (archiveAge === "expired" && a.tone !== "expired") return false;
                    }
                    return true;
                  };
                  if (!s && !archiveOnlyConfirmed && archiveAge === "all") return archive.length;
                  return `${archive.filter(matches).length} / ${archive.length}`;
                })()}
              </span>
              {archive.length > 0 && (
                <div className="flex items-center gap-2 normal-case tracking-normal flex-wrap">
                  {/* Filtro età (radio group compatto): follow-up 48h / scaduto 7g */}
                  <div className="inline-flex items-center rounded-full border border-[#E8E2D9] bg-white p-0.5" data-testid="q-archive-age-group">
                    {[
                      { id: "all", label: "Tutte", cls: "text-[#635F59]" },
                      { id: "stale", label: "≥48h", cls: "text-amber-700", activeCls: "bg-amber-500 text-white" },
                      { id: "expired", label: "Scaduti 7g", cls: "text-rose-700", activeCls: "bg-rose-600 text-white" },
                    ].map((opt) => {
                      const active = archiveAge === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setArchiveAge(opt.id)}
                          className={`h-6 px-2 rounded-full text-[11px] font-medium transition-colors ${active ? (opt.activeCls || "bg-[#1E3A5F] text-white") : `${opt.cls} hover:bg-[#F5F0E8]`}`}
                          title={
                            opt.id === "stale"
                              ? "Preventivi tra 48h e 7 giorni — da ricontattare (follow-up)"
                              : opt.id === "expired"
                                ? "Preventivi oltre i 7 giorni — non più validi, da rifare"
                                : "Mostra tutti i preventivi"
                          }
                          data-testid={`q-archive-age-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setArchiveOnlyConfirmed((v) => !v)}
                    className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[11px] font-medium transition-colors ${
                      archiveOnlyConfirmed
                        ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-white border-[#E8E2D9] text-[#635F59] hover:border-emerald-500/50 hover:text-emerald-700"
                    }`}
                    title="Mostra solo i preventivi che il cliente ha confermato (diventati prenotazioni reali)"
                    data-testid="q-archive-only-confirmed"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Solo confermati
                  </button>
                  <div className="relative w-56">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9b948a] pointer-events-none" />
                    <Input
                      className="pl-7 h-7 text-xs"
                      placeholder="Cerca N° o nome cliente…"
                      value={archiveSearch}
                      onChange={(e) => setArchiveSearch(e.target.value)}
                      data-testid="q-archive-search"
                    />
                    {archiveSearch && (
                      <button
                        onClick={() => setArchiveSearch("")}
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center rounded hover:bg-[#E8E2D9] text-[#9b948a]"
                        title="Pulisci"
                        data-testid="q-archive-search-clear"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {archive.length === 0 ? (
              <p className="text-[11px] text-[#635F59] italic">Nessun preventivo generato.</p>
            ) : (
              <div className="space-y-1.5">
                {(() => {
                  const s = archiveSearch.trim().toLowerCase();
                  const filtered = archive.filter((q) => {
                    if (s && !((q.quote_number || "").toLowerCase().includes(s) ||
                      (q.client_name || "").toLowerCase().includes(s) ||
                      (q.filename || "").toLowerCase().includes(s) ||
                      (q.client_phone || "").toLowerCase().includes(s))) return false;
                    if (archiveOnlyConfirmed && !(q.status === "confirmed" && q.converted_booking_code)) return false;
                    if (archiveAge !== "all") {
                      const a = quoteAgeInfo(q.generated_at);
                      if (!a) return false;
                      if (archiveAge === "stale" && a.tone !== "stale") return false;
                      if (archiveAge === "expired" && a.tone !== "expired") return false;
                    }
                    return true;
                  });
                  if (filtered.length === 0) {
                    let msg;
                    if (archiveAge === "stale" && !s && !archiveOnlyConfirmed) msg = "Nessun preventivo da ricontattare (48h–7g).";
                    else if (archiveAge === "expired" && !s && !archiveOnlyConfirmed) msg = "Nessun preventivo scaduto (>7g).";
                    else if (archiveOnlyConfirmed && !s && archiveAge === "all") msg = "Nessun preventivo confermato per ora.";
                    else msg = <>Nessun preventivo corrisponde ai filtri attivi{s ? <> per "<strong>{archiveSearch}</strong>"</> : null}.</>;
                    return (
                      <p className="text-[11px] text-[#635F59] italic" data-testid="q-archive-empty-search">{msg}</p>
                    );
                  }
                  return filtered.map((q) => {
                  const isConverted = q.status === "confirmed" && q.converted_booking_code;
                  const age = quoteAgeInfo(q.generated_at);
                  const ageBadgeCls = age?.tone === "expired"
                    ? "bg-rose-600 text-white"
                    : age?.tone === "stale"
                      ? "bg-amber-500 text-white"
                      : "bg-[#E8E2D9] text-[#635F59]";
                  const ageTitle = age?.tone === "expired"
                    ? "Preventivo scaduto (oltre 7 giorni dalla validità dichiarata nel PDF). Da rifare con tariffe aggiornate."
                    : age?.tone === "stale"
                      ? "Preventivo con più di 48h: valuta un follow-up con il cliente."
                      : "Preventivo recente (meno di 48h).";
                  return (
                  <div key={q.id} className={`flex items-center gap-2 border rounded px-2.5 py-1.5 text-xs ${isConverted ? "bg-emerald-50 border-emerald-200" : "bg-[#FAF7F2] border-[#E8E2D9]"}`} data-testid={`q-archive-${q.id}`}>
                    <FileText className={`w-3.5 h-3.5 shrink-0 ${isConverted ? "text-emerald-600" : "text-[#AC5D45]"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[#1E3A5F] truncate flex items-center gap-1.5">
                        {q.quote_number ? (
                          <span
                            className="px-1.5 py-0.5 rounded bg-[#AC5D45] text-white text-[10px] font-bold whitespace-nowrap tracking-wide"
                            title="Numero progressivo annuale del preventivo"
                            data-testid={`q-number-${q.id}`}
                          >
                            {q.quote_number}
                          </span>
                        ) : null}
                        <span className="truncate">{q.filename}</span>
                        {isConverted && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold whitespace-nowrap" title="Il cliente ha confermato il preventivo e si è creata una prenotazione">
                            ✓ CONFERMATO
                          </span>
                        )}
                        {!isConverted && age && age.tone !== "fresh" && (
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap uppercase tracking-wide ${ageBadgeCls}`}
                            title={ageTitle}
                            data-testid={`q-age-${q.id}`}
                          >
                            {age.tone === "expired" ? "Scaduto" : "Follow-up"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#635F59]">
                        {new Date(q.generated_at).toLocaleString("it-IT")}
                        {age ? <> · <span className={age.tone === "expired" ? "text-rose-700 font-medium" : age.tone === "stale" ? "text-amber-700 font-medium" : ""}>{age.label}</span></> : null}
                        {" · "}{q.client_name} · € {(q.total || 0).toFixed(2)}
                        {q.client_phone ? <> · <span className="text-emerald-700">{q.client_phone}</span></> : null}
                        {isConverted && (
                          <> · <a
                            href={`/admin/dashboard?tab=bookings&code=${q.converted_booking_code}`}
                            className="text-emerald-700 font-semibold underline hover:no-underline"
                            data-testid={`q-converted-link-${q.id}`}
                          >→ Prenotazione {q.converted_booking_code}</a></>
                        )}
                      </div>
                    </div>
                    {q.client_phone ? (
                      <button
                        type="button"
                        onClick={() => sendWhatsAppQuoteWithPdf(q)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded bg-[#25D366] hover:bg-[#1FBA56] text-white font-medium"
                        title={`Invia preventivo + link PDF a ${q.client_name} su WhatsApp`}
                        data-testid={`q-wa-${q.id}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                    ) : (
                      <span className="inline-flex items-center h-7 px-2 rounded bg-[#E8E2D9] text-[#9b948a] text-[10px] italic cursor-help" title="Salva il numero di telefono nel preventivo per inviare via WhatsApp">
                        no tel.
                      </span>
                    )}
                    <button onClick={() => previewArchived(q)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white" title="Anteprima PDF" data-testid={`q-preview-${q.id}`}>
                      <Eye className="w-3.5 h-3.5 text-[#AC5D45]" />
                    </button>
                    {!isConverted && age && age.tone === "stale" && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/admin/quotes/archive/${q.id}/followup-reset`);
                            const r = await api.post("/admin/quotes/followup/run");
                            const d = r.data || {};
                            if (d.sent >= 1) {
                              toast.success("Follow-up inviato su Telegram — controlla il bot per il link WhatsApp precompilato");
                            } else if (d.reason === "telegram_not_configured") {
                              toast.error("Telegram Bot non configurato — imposta TELEGRAM_BOT_TOKEN nel backend");
                            } else {
                              toast.info("Nessun candidato — potrebbe essere già stato notificato oppure fuori dalla finestra 48h–7g");
                            }
                          } catch (e) {
                            toast.error(errMsg(e, "Errore invio follow-up"));
                          }
                        }}
                        className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-amber-50"
                        title="Invia follow-up Telegram + link WhatsApp precompilato (48h dopo la generazione)"
                        data-testid={`q-followup-${q.id}`}
                      >
                        <Bell className="w-3.5 h-3.5 text-amber-600" />
                      </button>
                    )}
                    <button onClick={() => loadQuoteForEdit(q)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#E8E2D9]" title="Modifica e rigenera" data-testid={`q-edit-${q.id}`}>
                      <Edit3 className="w-3.5 h-3.5 text-[#7A5410]" />
                    </button>
                    <button onClick={() => downloadArchived(q)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white" title="Scarica">
                      <Download className="w-3.5 h-3.5 text-[#1E3A5F]" />
                    </button>
                    <button onClick={() => deleteArchived(q)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50" title="Elimina">
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    </button>
                  </div>
                  );
                });
                })()}
              </div>
            )}
          </div>
        </div>

      <PdfPreviewDialog
        open={!!pdfPreview}
        onClose={closePdfPreview}
        blobUrl={pdfPreview?.url}
        filename={pdfPreview?.filename}
      />
      </>
      )}
    </div>
  );
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};
